// TerrainMaterialManager — the renderer-facing program cache + per-chunk uniform binding for the
// Set9 Phase 3 render path.  Owns the assembled fragment shader (single material for now — dirt),
// the per-set WebGLProgram cache (D1), and the per-chunk uniform values (u_geoNormal — the
// per-sample geometric normal lattice from the worker's normals, u_baseAlbedo from the hypsometric
// ramp, and the recipe's param UBO).
//
// Lifecycle: created once per TerrainRenderer mount.  `ensureProgram` assembles + compiles the
// program for the active material set and returns a three.js `ShaderMaterial` template + a
// `UniformsGroup` (std140 UBO) carrying the recipe params.  Each chunk gets a material CLONE with
// its own per-chunk uniforms object; the UBO (params are set-wide constants) is SHARED across
// chunks — only u_geoNormal/u_baseAlbedo differ per chunk.
//
// The program cache (D1) is keyed by the structural hash of the material SET: a params-only
// recipe variant resolves to IDENTICAL GLSL → the same WebGLProgram (free, live-editable, no
// compile hitch).  Phase 6's warm-up pass enumerates by resolved hash for the same reason.

import * as THREE from "three";
import {RunInAction_Set} from "web-vcore";
import {
	assembleMaterialShader,
	VARIANT_PARAMS_COLUMNS,
	TERRAIN_REGION_CLIP_MAX,
	DEBUG_SHIM_MAX_MATERIALS,
	DEBUG_SHIM_STRUCTURAL_HASH,
	__testOnly_clearProgramCache,
} from "./Compose/ShaderAssembler.js";
import {collectUsedParams} from "./Compose/ShaderAssembler.js";

// NOTE: `terrainProcessStats` (TerrainProcessStats.ts) is imported LAZILY inside the Set13
// compile-counter methods (`noteCompileIssued`/`noteCompileResolved`) + `dispose`.  A top-level
// import would pull `TerrainProcessStats.ts` into the module graph of the BROWSER GPU test lane,
// which imports this file for `buildMaterialTemplate`/`compileRawFragmentProgram` — and that file
// uses `@O accessor` (auto-accessor) decorators that the browser lane's esbuild transform cannot
// parse (`SyntaxError: Invalid or unexpected token`).  The dynamic import keeps the static
// shape-builders (and the browser lane) free of that module, mirroring how `ProcMatAccessors.js`
// is imported lazily for the same reason (see below).
import {
	buildVariantParamLayouts,
	packVariantParamsRow,
	type VariantParamColumnLayout,
} from "./Compose/VariantParams.js";
import {
	packMaterialParams,
	type PackedParamLayout,
} from "./Compose/UniformPacker.js";
import {
	type ResolvedRecipe,
} from "./RecipeResolverCore.js";
import type {ParamValue} from "./@MaterialTypes.js";
import {packSplatChunk, SPLAT_MAX_MATERIALS, type SplatChunk} from "./Splat/SplatTypes.js";
import {ambientDetailUniformValues} from "./Compose/AmbientDetailStage.js";
// `SRGBToLinear` is exported from three's ColorManagement module but NOT re-exported by the
// top-level `three` entry (only the `ColorManagement` object is — same as `LinearToSRGB`, see
// Scenery/Bake/OutputTransfer.ts).  Used to decode the sRGB-chosen hypsometric ramp to linear.
import {SRGBToLinear} from "three/src/math/ColorManagement.js";

// NOTE: `ResolveRecipe`/`ResolveRecipeSet` (the per-map DB accessors) are imported LAZILY inside
// `ensureProgram`/`warmUpPrograms` — the only methods that need a mapID.  A top-level import here
// would pull `ProcMatAccessors` → `mobx-graphlink` → `@apollo/client` (CJS) into the Node vitest
// lane, which imports this module for the DB-free `buildMaterialTemplate` path.  The dynamic
// import keeps the static shape builders DB-free while the runtime resolve methods behave
// identically (the accessor module is cached by vite after first load).

/** Default recipe id for the Phase 3 single-material slice (D8: dirt first).  Phase 5's splat
 *  field replaces the single-recipe set with a ≤4-way evaluate-and-lerp. */
export const DEFAULT_MATERIAL_RECIPE_ID = "dirt";
/** Phase 4 — default strength of the normal-only specular lobe (plan §8).  A template-level
 *  constant for now (set-wide, not per-chunk/per-frame); 0 = disabled (byte-parity with the
 *  bake path).  Slider-backed once Phase 7 ships the shading-config UI. */
export const DEFAULT_SPECULAR_STRENGTH = 0.15;

/** Set11 Phase 2 Toksvig gain: widens per-fragment roughness by the sub-pixel slope variance, so a
 *  tight lobe does not alias into sparkle where relief is finer than a pixel.  0 = exact no-op. */
export const DEFAULT_VARIANCE_GAIN = 1;

/** 2026-08-25 — DEBUG SHIM recipe-id → colour/seed hash.  Deterministic + cheap (an FNV-1a-ish
 *  fold of the id's code points) so the same recipe always resolves to the same shim colour, but
 *  distinct ids spread out (see the golden-angle/decimal-golden-ratio spacing in ensureProgram). */
function hashRecipe(id: string): number {
	let h = 0x811c9dc5;
	for (let i = 0; i < id.length; i++) {
		h ^= id.charCodeAt(i);
		h = Math.imul(h, 0x01000193);
	}
	return (h >>> 0) / 0xffffffff;
}
/** Module-level shared vec4 array for the debug shim's per-slot colours (rgb = pre-pow colour,
 *  a = per-material seed for hue/strip orientation).  Cached so every debug program (all recipe
 *  sets collapse to ONE program) binds the SAME writable array — a per-program alloc is pointless
 *  since the value never changes. */
let matShimColors: THREE.Vector4[] | null = null;

/** The three.js ShaderMaterial template + its param UBO, produced by {@link ensureProgram}.
 *  Chunks share the program + UBO; per-chunk uniforms live on cloned materials. */
export interface TerrainMaterialProgram {
	/** Template material — clone per chunk (each clone gets its own uniforms object with the
	 *  per-chunk u_geoNormal/u_baseAlbedo values; the param UBO reference is shared). */
	material: THREE.ShaderMaterial;
	/** The std140 UBO carrying the recipe params (shared across all chunks). */
	paramGroup: THREE.UniformsGroup;
	/** std140 layout metadata (for packing param values). */
	paramLayouts: PackedParamLayout[];
	/** Structural hash of the active material SET (cache key). */
	setHash: string;
	/** The resolved active recipe (for param-override packing). */
	recipe: ResolvedRecipe;
	/** 2026-08-13 — this program's recipe-ids (slot order).  A params-only edit must refresh EVERY
	 *  cached program whose set includes the edited recipe (per-chunk programs are distinct sets),
	 *  so refreshParamValues re-resolves + re-packs each affected program's OWN UBO. */
	recipeIDs: string[];
	/** Set13 — true while this program's three-layer compileAsync is still in flight.  A
	 *  still-compiling program MUST NOT be rendered: three's first use (`setProgram ->
	 * getUniforms -> onFirstUse`) queries active uniforms/attributes, which forces the driver to
	 * finish the compile SYNCHRONOUSLY — the exact main-thread lockup Set13 exists to remove.
	 *  `getProgramForSet` therefore returns null until this flips false (so the renderer's SD3
	 *  hold-hidden path keeps chunks invisible), and `ensureProgram` awaits it before returning. */
	compiling?: boolean;
	/** Set13 — resolves when this program's compile completes (link landed).  Same promise
	 *  `compiling` tracks; awaiting `compiling`-bearing programs is the ONE sanctioned way to
	 *  wait for draw-readiness without re-dispatching a warm. */
	compiledPromise?: Promise<void>;
}

/** Set6 (2026-08-25) → Set7 (2026-08-25) — a Layer-2 VARIANT: a Layer-1 program + THIS identity
 *  set's param VALUES (as a ROW in the shared variant-params texture).  Layer-1 programs are keyed
 *  by STRUCTURAL hash set, so two distinct recipes that are structurally identical (e.g. Grass3 vs
 *  Grass_Jungle — same layers, same param KEYS, differing only in param default VALUES) collapse
 *  to ONE program whose shared param UBO carries whichever identity compiled first.  A Layer-2
 *  variant decouples the param VALUES from the program+UBO: under Set6 each identity set got its
 *  OWN UBO (per-identity param values); under Set7 the values move into a single shared RGBA32F
 *  texture (`u_variantParams`), one row per identity — so a variant is just a ROW INDEX + the
 *  packed Float32Array row, and the per-material bind is a plain uniform write (not a UBO, which
 *  three.js binds per-PROGRAM — the Set6 Finding-A correctness bug).
 *
 *  Chunks that share a recipe-identity set share the same variant (same row); a chunk whose
 *  identity set differs from the program's compile-time set binds ITS OWN row instead of the
 *  program's default values. */
export interface TerrainMaterialVariant {
	/** The Layer-1 program this variant derives from (shared GL program + layout metadata). */
	program: TerrainMaterialProgram;
	/** This variant's row index in the shared variant-params texture (row 0 = the all-defaults
	 *  fallback row).  Set on each chunk's material via {@link setChunkVariantRow}. */
	row: number;
	/** This variant's OWN param column layout — assembled from the identity set's resolved recipes
	 *  in `collectUsedParams` order, aligned to the shared GLSL's variant-params cursor (and the
	 *  UBO's `u_p_<index>` refs).  Distinct from `program.paramLayouts`. */
	paramLayouts: VariantParamColumnLayout[];
	/** This identity set's packed Float32Array row (RGBA texel-major).  Owned by the manager's
	 *  shared texture; rewritten in place on a param edit. */
	values: Float32Array;
	/** The recipe-identity set this variant's row was packed from (slot order). */
	recipeIDs: string[];
	/** Layer-2 cache key (the identity set + flags; mirrors setMemoKey). */
	key: string;
}

/**
 * Renderer-side manager for the procedural-materials render path.  Constructed without a GL
 * context; `ensureProgram` needs the renderer's WebGL2 context (deferred until the renderer's
 * gl is available).  Each chunk mesh shares ONE program; the recipe-param values are carried by a
 * Layer-2 UBO (per recipe-identity set — {@link getVariantForSet}) bound to the chunk's material
 * clone.  Per-chunk uniforms (u_geoNormal, u_baseAlbedo) differ per mesh, so they live on the
 * mesh's cloned material uniforms.
 */
export class TerrainMaterialManager {
	constructor(public mapID: string) {
		// Position the warm camera so the z=0 scratch plane is inside the near(0.1)..far(10)
		// frustum — otherwise the warm render frustum-culls the scratch and never links its
		// program (the "warm materials exist but no program is ever compiled" bug, 2026-08-25).
		this.warmCamera.position.z = 1;
		this.warmCamera.lookAt(0, 0, 0);
		this.warmCamera.updateMatrixWorld();
	}
	private gl: WebGL2RenderingContext | null = null;
	/** The three.js renderer the manager pre-warms through (attached by the renderer; null when
	 *  not yet available).  Used ONLY for the Phase 6 three.js-layer warm-up (compiling scratch
	 *  ShaderMaterials into three's program cache) — never for rendering the terrain. */
	private threeRenderer: THREE.WebGLRenderer | null = null;
	/** The LIVE scene + camera (R3F root).  The warm scratch render uses them so three computes
	 *  the program cacheKey with the SAME lights/shadows/output as the live chunk render — any
	 *  divergence (light counts, numDirLightShadows, outputColorSpace) ⇒ different key ⇒ no dedup
	 *  ⇒ every chunk compiles cold (the 2026-08-25 zoom stutter). */
	private liveScene: THREE.Scene | null = null;
	/** The LIVE camera (paired with liveScene). */
	private liveCamera: THREE.Camera | null = null;
	/** Provides the LIVE renderer's output config (toneMapping + outputColorSpace) so the warm
	 *  render pins the SAME values the chunk renders will use.  three folds both into the program
	 *  cacheKey, and they're set by ToneMappingController per-frame (NOT at renderer creation) —
	 *  so a warm render against the renderer's defaults (srgb-linear) produces a DIFFERENT key
	 *  than the live render (srgb) ⇒ no dedup ⇒ cold compile.  Set by the renderer. */
	private outputConfig: (() => {toneMapping: number; outputColorSpace: string}) | null = null;
	/** Phase 6 — scratch ShaderMaterials holding the pre-warmed three.js programs alive until the
	 *  live template material appears (a three.js program is destroyed when its last referencing
	 *  material is disposed/GC'd — `usedTimes` hits 0 — so the warm-up MUST keep the scratch
	 *  materials referenced across the idle → first-appearance gap).  Keyed by set hash. */
	private warmMaterials = new Map<string, {material: THREE.ShaderMaterial; setHash: string}>();
	/** The PROGRAM cache: set-hash → the assembled three.js template + its param UBO.  P4 — one
	 *  entry per DISTINCT per-chunk material set (the plan's per-chunk program key): a chunk's
	 *  program is fetched by its own recipe set, not a single map-wide set.  The module-scoped
	 *  WebGLProgram cache in ShaderAssembler is keyed by the same set hash, so re-entry with an
	 *  already-compiled set is a module-cache hit (D1). */
	private programs = new Map<string, TerrainMaterialProgram>();
	/** Set13 — the number of three-layer compiles ISSUED this warm-up/heal wave and still
	 *  awaiting driver completion (`compileAsync` in flight).  Mirrored to
	 *  `terrainProcessStats.materialCompilesPending` (the "Compiling shaders (X/Y)" label).
	 *  Incremented on dispatch in `warmUpPrograms`/`ensureProgram`'s async pre-link, decremented
	 *  on each resolution.  A wave is "done" when it returns to 0 (the label disappears). */
	private materialCompilesPending = 0;
	/** Set13 — the total number of compiles issued this wave (the Y in "X/Y"), i.e. the count
	 *  recorded when the current wave STARTED (issued - still-remaining at issue time is not
	 *  stable as new heals dispatch mid-wave).  See `noteCompileIssued`/`noteCompileResolved`. */
	private materialCompilesTotal = 0;
	/** P4 — memo of recipe-id-list → set hash (the sync {@link getProgramForSet} cannot resolve
	 *  recipes itself; the async ensure/warm paths record every list they resolved).  Keyed by
	 *  the id list IN SLOT ORDER + the splat/regionParams flags (the same flags that fold into
	 *  the set hash).  **2026-08-29 — ORDER-SENSITIVE**: the set-hash is the structural-hash
	 *  SEQUENCE (not a sorted multiset), so the id-list key must be order-sensitive too, or a
	 *  chunk whose slot order differs from the warmed one MISSES the memo and falls back to a
	 *  wrong program (the GrassInsideSnow snow→water bug — and, with the old sorted key, the
	 *  2026-08-29 Grass↔Grass_Jungle variant-row swap; see Set8). */
	private resolvedSetHashByIds = new Map<string, string>();

	/** Set6 (2026-08-25) → Set7 — Layer-2 VARIANT cache: recipe-identity set → {program, row,
	 *  paramLayouts, values}.  Keyed by the identity set + the splat/regionParams flags (same
	 *  flags as the Layer-1 set hash).  A distinct identity set that shares a Layer-1 structural
	 *  hash gets its OWN ROW here (packed from that identity's resolved defaults), while all such
	 *  variants reference the SAME Layer-1 program + the SAME shared variant texture — O(1) in
	 *  variant count (the Set7 replacement for Set6's per-variant UBO). */
	private variants = new Map<string, TerrainMaterialVariant>();

	// ── Set7 — the shared variant-params texture + row allocator ──────────────────────────────
	/** The single shared RGBA32F DataTexture carrying EVERY identity variant's param VALUES: one
	 *  ROW per identity set; columns = `variantRowFloatWidth` floats laid out as RGBA texels.
	 *  Row 0 is reserved as the all-defaults fallback.  All chunk materials share this ONE
	 *  texture (a plain `int u_variantRow` uniform selects the row per chunk — Finding A's
	 *  per-material upload, §2.2).  Lazily created on the first variant record. */
	private variantTexture: THREE.DataTexture | null = null;
	/** Float width of a variant row (texel width = /4).  Grows (and the texture is rebuilt) when
	 *  a set's used params exceed the current width. */
	private variantRowFloatWidth = VARIANT_PARAMS_COLUMNS;
	/** Next free row in the variant texture (row 0 = the reserved fallback row). */
	private nextVariantRow = 1;
	/** Guard 2 (Set7 §9.5) — a FREE-LIST of row indices reclaimed by {@link releaseVariantRow}
	 *  (identity sets no longer referenced by any live chunk).  `packVariantRow` reuses a freed
	 *  row before allocating a new one, so panning across combinatorial set growth recycles rows
	 *  instead of accumulating them (and the backing Float32Array stops growing unbounded).
	 *  A row is only freed AFTER the last live chunk material holding its `u_variantRow` value is
	 *  released (the eviction ordering constraint — see releaseVariantRow). */
	private variantRowFreeList: number[] = [];
	/** Guard 2 (§9.5) — reference count per variant (setMemoKey → live chunk count).  A variant
	 *  with refcount 0 is eligible for row eviction.  `releaseVariantRow` is called from the
	 *  chunk-eviction lifecycle in the renderer. */
	private variantRowRefs = new Map<string, number>();
	/** The packed float data backing `variantTexture` (row-major: row r starts at
	 *  r * variantRowFloatWidth — one row = variantRowFloatWidth floats = widthTexels * 4 RGBA
	 *  texels; NOT *4 again).  Written by the packers; `variantTexture.needsUpdate` set after
	 *  writes. */
	private variantData: Float32Array | null = null;

	/** Set7 — ensure the shared variant texture exists with at least `floatWidth` columns,
	 *  growing + repacking if a wider row is needed.  Returns the current instance.
	 *  Guard 1 (Set7 §9.4) — the row width is bounded against `MAX_TEXTURE_SIZE`: the texture's
	 *  width-in-texels must stay ≤ the GL cap (spec floor 2048), else the DataTexture upload
	 *  fails or truncates silently.  A ramp-heavy 8-slot set is the risk case; we throw a named,
	 *  actionable error at the growth point rather than let the texture break. */
	private ensureVariantTexture(floatWidth: number): THREE.DataTexture {
		if (floatWidth > this.variantRowFloatWidth) this.variantRowFloatWidth = Math.ceil(floatWidth / 4) * 4;
		const widthTexels = Math.floor(this.variantRowFloatWidth / 4);
		// Guard 1 (§9.4): bound the auto-grown row width against MAX_TEXTURE_SIZE.  WebGL2
		// guarantees >= 2048; the dev context reports 8192.  A width beyond the cap would make
		// `new THREE.DataTexture(...)` (or the GL upload) fail/truncate — surface it as a named
		// error.  (The variant texture's HEIGHT also can't exceed MAX_TEXTURE_SIZE, but rows are
		// ~200-1200 texels wide and the count is bounded by live chunk sets; the width is the
		// §9.4 risk.)
		const maxTexSize = this.gl?.getParameter(this.gl.MAX_TEXTURE_SIZE) as number | undefined;
		if (maxTexSize != null && widthTexels > maxTexSize) {
			throw new Error(`[TerrainMaterialManager] variant row width ${widthTexels} texels exceeds MAX_TEXTURE_SIZE ${maxTexSize} — reduce ramp params or split the set (Set7 §9.4)`);
		}
		/** Flush three.js's upload caches before re-assigning the variant texture's image.
		 *  three 0.174 uploads every non-video texture as IMMUTABLE texStorage2D allocated once
		 *  (allocateMemory = sourceProperties.__version === undefined). A plain needsUpdate (or
		 *  even dispose(), which clears the TEXTURE's properties but NOT its Source's __version)
		 *  then only issues texSubImage2D at the NEW dims into the OLD smaller immutable
		 *  storage → GL_INVALID_VALUE "Offset overflows texture dimensions" → the grown rows
		 *  never reach the GPU → pitch-black deep-zoom chunks. Installing a FRESH Source object
		 *  (plus dispose of the texture's own cache; same JS object → all bound uniforms stay
		 *  valid) makes the next draw re-allocate storage at the new dims via texImage2D. */
		const installFreshImage = (data: Float32Array, w: number, h: number) => {
			const tex = this.variantTexture;
			if (tex == null) return; // unreachable: every caller checks first
			tex.dispose();
			tex.image = {data, width: w, height: h};
			tex.source = new THREE.Source(tex.image);
			tex.needsUpdate = true;
		};
		if (this.variantTexture == null || this.variantData == null) {
			// Row 0 reserved: an all-defaults row (all zeros) — the fallback for a never-recorded
			// identity (the manager's getVariantForSet null case renders the program default, but
			// row 0 keeps any stray u_variantRow = 0 read from hitting garbage).
			const data = new Float32Array(1 * widthTexels * 4);
			const tex = new THREE.DataTexture(data, widthTexels, 1, THREE.RGBAFormat, THREE.FloatType);
			tex.minFilter = THREE.NearestFilter;
			tex.magFilter = THREE.NearestFilter;
			tex.generateMipmaps = false;
			tex.wrapS = THREE.ClampToEdgeWrapping;
			tex.wrapT = THREE.ClampToEdgeWrapping;
			tex.needsUpdate = true;
			this.variantTexture = tex;
			this.variantData = data;
			this.nextVariantRow = 1;
		} else if (widthTexels > this.variantTexture.image.width) {
			// Grow: repack existing rows into a wider row.  2026-08-26 — mutate the SAME
			// DataTexture instance (reassign image + needsUpdate) rather than dispose+recreate:
			// a recreated texture orphans every already-bound u_variantParams uniform (they hold
			// the disposed instance), which sampled zeros → deep-zoom black chunks.
			const oldW = this.variantTexture.image.width * 4;
			const oldHeight = Math.max(this.variantTexture.image.height, 1);
			const copyRows = Math.max(this.nextVariantRow, oldHeight);
			const newData = new Float32Array(copyRows * widthTexels * 4);
			for (let r = 0; r < copyRows; r++) {
				newData.set(this.variantData.subarray(r * oldW, (r + 1) * oldW), r * widthTexels * 4);
			}
			this.variantData = newData;
			// Flush three's immutable storage cache (see installFreshImage comment above).
			installFreshImage(newData, widthTexels, copyRows);
		}
		// Keep the texture's height aligned to the current row count (all rows within bounds).
		// 2026-08-26 (black-chunk fix): NEVER mutate image.height alone. Growing height without
		 // reallocating `data` left the CPU array and the declared image dims out of sync; after
		// ANGLE's robustTexSubImage2D ran bounds checks against the mismatched extents, whole
		// uploads were REJECTED (GL_INVALID_VALUE "Offset overflows texture dimensions") —
		// the pushed rows never reached the GPU and their chunks rendered pitch black.
		// Growth now goes through exactly ONE path below: reallocate `variantData` to fit the
		// new dims (zero-filling any tail), reassign image in place, mark needsUpdate once.
		if (this.variantTexture.image.height < this.nextVariantRow || this.variantData.length < this.nextVariantRow * widthTexels * 4) {
			const rows = Math.max(this.nextVariantRow, Math.ceil(this.variantData.length / (widthTexels * 4)), 1);
			const sized = new Float32Array(rows * widthTexels * 4);
			sized.set(this.variantData.subarray(0, Math.min(this.variantData.length, sized.length)));
			this.variantData = sized;
			// Same flush as the width-grow path above (fresh Source → texStorage2D re-alloc).
			installFreshImage(sized, widthTexels, rows);
		}
		return this.variantTexture;
	}

	/** Set7 — return the shared variant texture (creating it if needed).  Used by the GPU tests
	 *  and by setChunkVariantRow's binding path. */
	getVariantTexture(): THREE.DataTexture {
		return this.ensureVariantTexture(this.variantRowFloatWidth);
	}

	/** The ORDER-SENSITIVE memo key for a recipe set: the ids in the given (slot) order + the
	 *  splat/regionParams flags (matching the set-hash's flag suffix).  2026-08-29 — the variant
	 *  row MUST be packed in the SAME recipe order as the chunk's splat slots: `eval_i` reads the
	 *  `i`-th contiguous param block, so a chunk whose slot order differs from another chunk with
	 *  the same member set (a dominance inversion between regions, e.g. grass vs Grass_Jungle)
	 *  needs its OWN row packed in ITS order.  Sorting here would collapse both orders onto one
	 *  row → param blocks misaligned → the Grass↔Grass_Jungle spatial swap (Set8). */
	private setMemoKey(ids: readonly string[], flagKey: string): string {
		return ids.join(",") + flagKey;
	}
	/** The most recently used program — the frame loop clones chunk materials from this when the
	 *  chunk's set matches (the overwhelmingly common single-set case stays allocation-free). */
	private program: TerrainMaterialProgram | null = null;
	private setHash = "";
	/** 2026-08-25 — DEBUG SHIM (vscode integrated-browser perf escape hatch).  When true, every
	 *  assembled program is the tiny single-compile hashed-colour shim (see AssembleOptions
	 *  .debugShim) with a FIXED structural hash → all recipe sets share ONE WebGLProgram.  Set by
	 *  the renderer from the MapSettings debugShimMats toggle; a toggle clear-attaches so the next
	 *  ensureProgram recompiles the real shaders. */
	private __debugShim = false;

	/** 2026-08-25 — set (or clear) the debug-shim mode.  Enabling swaps every cached/ensured
	 *  program for the single trivial hashed-colour shim; disabling restores the real material
	 *  shaders.  Per-set programs are dropped either way so the next ensureProgram/warmUpPrograms
	 *  (the renderer re-schedules them on the setting change) compiles fresh.  Returns its arg. */
	__setDebugShim(v: boolean): boolean {
		if (this.__debugShim === v) return v;
		this.__debugShim = v;
		// Set13 — disposing a material whose compileAsync is STILL IN FLIGHT forces the driver
		// to finish/abort that compile synchronously (the same lockup the shield exists to
		// prevent, triggered by teardown instead of rendering).  Never dispose a still-compiling
		// program synchronously: await its `compiledPromise` FIRST, then dispose.
		void this.disposeProgramsSafely();
		return v;
	}
	/** Set13 — dispose every cached program material WITHOUT the still-compiling lockup: the
	 *  settled ones go immediately; each in-flight one is disposed only after its compile
	 *  completes (awaited — the await keeps the main thread free while the driver finishes).
	 *  The warm SCRATCH for each set is disposed alongside its program — the scratch is what
	 *  holds the GL program alive (usedTimes), so disposing it while the compile is in flight
	 *  would release+delete an un-linked program (the same driver lockup, via teardown). */
	private async disposeProgramsSafely(): Promise<void> {
		/** Dispose the set's template + scratch (both settled-program safe). */
		const disposeProgramAndScratch = (setHash: string, prog: TerrainMaterialProgram)=>{
			prog.material.dispose();
			this.programs.delete(setHash);
			const scratch = this.warmMaterials.get(setHash);
			if (scratch) { scratch.material.dispose(); this.warmMaterials.delete(setHash); }
		};
		const inFlight: Promise<void>[] = [];
		/** The set hashes whose compile is still in flight — their scratch materials must NOT be
		 *  disposed yet (each scratch holds a program reference; disposing it mid-compile can drop
		 *  usedTimes → 0 → deleteProgram on an un-linked program — the same driver lockup). */
		const inFlightHashes = new Set<string>();
		for (const [setHash, prog] of this.programs) {
			if (prog.compiling === true && prog.compiledPromise) {
				inFlightHashes.add(setHash);
				inFlight.push(prog.compiledPromise.then(()=>{
					// The manager may have re-populated during the await (a re-ensure raced the
					// dispose); the cache's CURRENT entry decides — never dispose a foreign program.
					if (this.programs.get(setHash) === prog) disposeProgramAndScratch(setHash, prog);
				}));
			} else {
				// Already settled (or never warmed — headless/test): dispose immediately.
				disposeProgramAndScratch(setHash, prog);
			}
		}
		// Sweep the remaining scratches — skipping the in-flight sets (deferred above).
		for (const [setHash, {material}] of this.warmMaterials) {
			if (inFlightHashes.has(setHash)) continue;
			material.dispose();
			this.warmMaterials.delete(setHash);
		}
		// Only strip the bookkeeping once every program material is actually (or scheduled to
		// be) disposed.  A synchronous caller (the renderer's unmount) has already stopped
		// rendering, so live memo entries during the await window are harmless.
		if (inFlight.length > 0) await Promise.all(inFlight);
		this.program = null;
		this.setHash = "";
		this.resolvedSetHashByIds.clear();
	}
	/** Whether debug-shim mode is active. */
	get isDebugShim(): boolean { return this.__debugShim; }

	/** Provide (or update) the WebGL context.  Called by the renderer once the gl is available. */
	attachGL(gl: WebGL2RenderingContext): void {
		if (this.gl === gl) return;
		this.gl = gl;
		this.programs.clear();
		this.variants.clear();
		this.resolvedSetHashByIds.clear();
		this.program = null;
		this.setHash = "";
	}

	/** **Test-only seed** — inject a program directly into the per-set cache (bypassing
	 *  ensureProgram/GL), to test refreshParamValues's multi-program refresh. */
	__testOnly_seedProgram(setHash: string, prog: TerrainMaterialProgram): void {
		this.programs.set(setHash, prog);
		this.program = prog;
		this.setHash = setHash;
	}

	/** Set6 debug — introspection for Issue A (right-side Grass-vs-Grass3): expose each Layer-1
	 *  program's compile-time recipe ids + param-layout keys, and the packed values of a variant's
	 *  ROW for a given identity set.  Used to verify that a variant's overrides actually reach
	 *  matching layout slots (a variant whose identity set has recipes NOT in the program's
	 *  compile-time set cannot vary those slots — the shared-layout limitation). */
	__debugVariantInfo(recipeIDs: string[], opts: {useSplat?: boolean; useRegionParams?: boolean} = {}): any {
		const useSplat = opts.useSplat === true;
		const useRegionParams = opts.useRegionParams === true && useSplat;
		const flagKey = (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : ":variantParams");
		const key = this.setMemoKey(recipeIDs, flagKey);
		const variant = this.variants.get(key);
		const prog = variant?.program ?? this.programs.get(this.resolvedSetHashByIds.get(key) ?? "");
		const out: any = {variantKey: key};
		if (variant) {
			out.variantExists = true;
			out.variantRow = variant.row;
			out.programRecipeIDs = variant.program.recipeIDs;
			out.programParamLayoutKeys = variant.program.paramLayouts.map(l=>l.key);
			// The variant's OWN layout keys (THIS identity set's recipe ids — e.g. Grass3/...).
			out.variantParamLayoutKeys = variant.paramLayouts.map(l=>l.paramKey);
			// The variant's packed values (as stored in its row of the shared texture), for scalars.
			const scalars: any = {};
			for (const l of variant.paramLayouts) {
				if (l.kind == "scalar") scalars[l.paramKey] = variant.values[l.column];
			}
			out.variantScalarValues = scalars;
		} else {
			out.variantExists = false;
			out.programRecipeIDs = prog?.recipeIDs ?? null;
		}
		return out;
	}

	/** Attach the three.js renderer (for the Phase 6 three.js-layer program pre-warm).  The
	 *  renderer is created on the SAME gl context; the manager never renders through it for the
	 *  terrain — it only calls `renderer.compile`/`render` on scratch scenes during idle. */
	attachRenderer(renderer: THREE.WebGLRenderer): void {
		if (this.threeRenderer === renderer) return;
		this.threeRenderer = renderer;
		// A new renderer = a fresh three.js program cache — previous warm-ups are meaningless.
		this.warmMaterials.clear();
		// Set13 — three's supported shader-error hook: route three-layer compile/link failures
		// into a labelled console.error the app's fallback logic can react to.  three logs its
		// own info-log via `checkShaderErrors` (console message, NOT an exception), and our
		// post-async LINK_STATUS probe catches the same failure on the compileAsync path — this
		// hook restores programmatic visibility for the whole three layer (Set13 §2 mitigation).
		renderer.debug.onShaderError = (_gl, _program, glVertexShader, glFragmentShader)=>{
			const vLog = _gl.getShaderInfoLog(glVertexShader);
			const fLog = _gl.getShaderInfoLog(glFragmentShader);
			console.error(`[TerrainMaterialManager] three-layer shader error (falling back to the legacy bake path). vertex:\n${vLog}\nfragment:\n${fLog}`);
		};
	}

	/** Attach the LIVE scene + camera so the warm scratch renders use them (matching the program
	 *  cacheKey the live chunk renders will produce — see the liveScene docblock). */
	attachScene(scene: THREE.Scene | null, camera: THREE.Camera | null): void {
		this.liveScene = scene;
		this.liveCamera = camera;
	}

	/** Attach a getter for the LIVE renderer's output config (toneMapping + outputColorSpace),
	 *  so the warm render pins the same values the chunk renders use (see the outputConfig
	 *  docblock — three folds both into the program cacheKey). */
	attachOutputConfig(getter: (() => {toneMapping: number; outputColorSpace: string}) | null): void {
		this.outputConfig = getter;
	}

	/** Whether a program is currently assembled (any set). */
	get isReady(): boolean { return this.programs.size > 0; }
	/** The number of DISTINCT assembled programs in the per-set cache.  Surfaces the "how many
	 *  shader programs does this map generate" diagnostic (Set9 follow-up): structurally-identical
	 *  recipe sets collapse onto one program (D1), so a low count here means the region layout is
	 *  being served by few compiled shaders; a count approaching the number of distinct per-chunk
	 *  recipe sets means many shaders.  Read by the Stats panel. */
	get programCount(): number { return this.programs.size; }

	/** Resolve + assemble + compile the active material set (default: the dirt slice). Idempotent when the structural hash is unchanged (D1 free-variant). Phase 5: pass `useSplat: true` for the ≤4-way splat shader (the renderer passes the chunk's recipe ids). Phase 7: pass `useRegionParams: true` to emit the per-region params texture path (regionModulated params read from the texture instead of the UBO).  P4: the program cache is keyed PER SET — each distinct per-chunk recipe set gets its own template + UBO, and the LAST-ensured set is kept as the fast-path `this.program` (the frame loop clones chunk materials from it when the chunk's set matches). */
	async ensureProgram(
		recipeIDs: string[] = [DEFAULT_MATERIAL_RECIPE_ID],
		opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number} = {},
	): Promise<TerrainMaterialProgram> {
		const gl = this.gl;
		if (!gl) throw new Error("[TerrainMaterialManager] no WebGL context attached");
		const {ResolveRecipe} = await import("../../Server/DB/mapObjects/ProcMatAccessors.js");
		const resolved = recipeIDs.map(id=>ResolveRecipe(id, this.mapID));
		const useSplat = opts.useSplat === true;
		const useRegionParams = opts.useRegionParams === true && useSplat;
		const useVariantParams = opts.useVariantParams !== false && useSplat;
		if (useSplat && resolved.length > SPLAT_MAX_MATERIALS) throw new Error(`[TerrainMaterialManager] splat mode supports ≤${SPLAT_MAX_MATERIALS} recipes (got ${resolved.length})`);
		// 2026-08-25 — DEBUG SHIM: when active, every set collapses to ONE fixed structural hash
		// (the shim's fragment string is content-constant), so all recipe sets share a single
		// WebGLProgram — the whole point of the escape hatch (no per-set/region compile).
		const debugShim = this.__debugShim;
		// Assemble with lighting (Phase 3). Phase 5: the cache key folds the splat flag (a splat
		// shader differs from the single-material one even for the same set). Phase 7: the region-
		// params flag too (a shader with the region texture differs from one without). splatSize /
		// regionParamsColumns are NOT folded — compile-time constants the uploader must match.
		// 2026-08-29 — ORDER-SENSITIVE set hash (the structural-hash SEQUENCE, NOT a sorted
		// multiset): the GLSL `eval_i` order is order-sensitive, so a chunk whose slot order
		// differs from another chunk's (a dominance inversion, e.g. grass vs Grass_Jungle) needs
		// a program compiled in ITS order.  Structurally-identical recipes (grass == Grass_Jungle
		// == 7f59b754) still collapse (identical sequence either way) — zero extra programs — and
		// the variant ROW (setMemoKey) carries the per-order values.  See Set8.
		const setHash = debugShim
			? DEBUG_SHIM_STRUCTURAL_HASH + (useSplat ? ":lit:splat" : ":lit")
			: resolved.map(r=>r.structuralHash).join("~")
				+ (useSplat ? ":lit:splat" : ":lit")
				+ (useRegionParams ? ":regionParams" : "")
				+ (useVariantParams ? ":variantParams" : "");
		// P4 — per-set cache: a chunk whose set was already ensured (its own set, or a set that
		// resolved to the same structural hash — D1 params-only free variants) is a cache hit.
		this.resolvedSetHashByIds.set(this.setMemoKey(recipeIDs, (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + (useVariantParams ? ":variantParams" : "")), setHash);
		const existing = this.programs.get(setHash);
		if (existing) {
			// Set13 — a cache hit on a program whose compileAsync is still in flight is NOT
			// draw-ready: awaiting its `compiledPromise` guarantees this ensureProgram resolves
			// only once the link landed (a caller that then renders `existing` can never trigger
			// three's synchronous onFirstUse uniform-query stall on an un-compiled program).
			if (existing.compiling) await existing.compiledPromise;
			this.program = existing; this.setHash = setHash;
			// Set6/7 — record the Layer-2 variant for THIS identity set (its row packed from these
			// resolved recipes) even on a cache hit, so a different identity sharing the program's
			// structural hash still gets its own correct row.  A no-op when already recorded.
			// (Skipped under the debug shim — the shim has no per-identity param rows, and every
			// set shares the one shim program.)
			if (!debugShim) this.recordVariant(recipeIDs, existing, resolved, (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + (useVariantParams ? ":variantParams" : ""), opts);
			return existing;
		}

		// Compile the program through three's layer only (Set13 — async).  The fragment source is
		// the assembled GLSL (self-contained, `#version 300 es` included); the vertex side is
		// three's standard ShaderMaterial vertex (supplied below).  The raw module-cache compile
		// (compileMaterialProgram → compileRawFragmentProgram) is GONE from this live path — it
		// was the second synchronous GL compile that blocked the main thread (Set13 §1).  GLSL
		// validation is retained in the browser GPU test lanes (Materials.gpu.test.ts /
		// ShaderParse.test.ts) which still use the raw helper directly, and error surfacing on
		// the live path comes from `renderer.debug.onShaderError` + the post-async LINK_STATUS
		// probe (see warmMaterialForProgram).
		// 2026-08-25 — SKIPPED under the DEBUG SHIM: `compileMaterialProgram` assembles the REAL
		// (non-shim) shader (it doesn't take debugShim opts), which is exactly the expensive
		// compile we're avoiding.  The shim's fragment is trivial + content-constant (one
		// WebGLProgram for the whole terrain), so there is no module-cache worth pre-populating —
		// three compiles the shim on first render and reuses it forever.

		// Re-derive the program object — compileMaterialProgram returns the GL program but the
		// three ShaderMaterial needs three's own program caching; to keep D1's cache semantics
		// (params-only variants share a program) we compile the fragment ourselves via the
		// module cache and build a ShaderMaterial on top (three compiles it on first render,
		// deduped by fragment source — identical source → identical GL program).
		const result = assembleMaterialShader(resolved, {
			includeLighting: true, useSplat, splatSize: opts.splatSize,
			useRegionParams, regionParamsColumns: opts.regionParamsColumns,
			useVariantParams, variantRowColumns: opts.variantRowColumns,
			// 2026-08-25 — DEBUG SHIM (vscode integrated-browser perf escape hatch): when active,
			// assemble the tiny single-compile hashed-colour shim instead of the real material.
			debugShim,
			// Enabled 2026-08-09 (the Set11 Phase 2 "flip in a follow-up" TODO): without this the lit
			// call is the 3-arg hardcoded-pow(...,32) form and every recipe's `roughness` is DEAD (the
			// eval writes s.roughness and nothing reads it).  MUST match the other two assemble sites
			// below — the program cache is keyed on content, so a mismatch pre-warms the wrong program.
			roughnessAwareSpecular: true,
			// 2026-08-13 — restore the legacy bake's terrain-region clipping (see assembleMaterialShader).
			terrainRegionClip: true,
			// Set14 P7 §4.11 — the output transfer chain: tone-map + sRGB-encode out_Color via
			// three's LIVE functions (injected per the renderer's toneMapping/outputColorSpace),
			// so the terrain sits on the SAME chain as the MeshStandardMaterial trees (closes the
			// P4 tree↔terrain brightness/contrast gap). MUST match the warm-up site below — the
			// program cache is keyed on content.
			outputTransfer: true,
		});
		const paramLayouts = result.paramLayouts;
		const paramGroup = this.buildParamGroup(paramLayouts);
		const material = buildMaterialTemplate(result.glsl, useSplat, paramLayouts, THREE, ()=>paramGroup, useRegionParams, useVariantParams);
		if (debugShim) {
			// The shim's per-slot hashed colours (rgb = pre-pow colour, a = per-material seed for
			// hue/strip orientation) — derived once per SLOT INDEX from the recipe's id hash, so
			// distinct materials read as distinct colours/bands.  Same vector on every material
			// (the program is shared) — the per-slot identity comes from the splat index.
			if (!matShimColors) matShimColors = new Array(DEBUG_SHIM_MAX_MATERIALS).fill(null).map(()=>new THREE.Vector4(0, 0, 0, 0));
			const cols = matShimColors; // local alias — non-null after the init above
			resolved.forEach((r, i) => {
				if (i >= DEBUG_SHIM_MAX_MATERIALS) return;
				const c = cols[i];
				const h = hashRecipe(r.id);
				const fr = (x: number): number => x - Math.floor(x);
				c.set(
					0.25 + 0.75 * fr(h * 0.6180339887498949),
					0.30 + 0.70 * fr(h * 1.6180339887498949),
					0.35 + 0.65 * fr(h * 2.6180339887498949),
					h, // seed → hue + strip orientation (already [0,1))
				);
			});
			// Bind as a vec4 array uniform on the template (clones share it; three skips it when
			// a slot is unused by the GLSL's fixed 8-slot declaration — harmless).
			(material.uniforms.u_debugShimColors as {value: THREE.Vector4[]}).value = cols;
		}

		const prog: TerrainMaterialProgram = {material, paramGroup, paramLayouts, setHash, recipe: resolved[0], recipeIDs};
		// Set6/7 — record the Layer-2 variant for the fast-path identity set (its own ROW).  The
		// program's default `paramGroup` already carries these values; the variant's row is packed
		// from this same identity — recorded under the identity key so OTHER chunks requesting
		// this identity set bind it (and a different identity sharing the hash gets its own row).
		// (Skipped under the debug shim — no per-identity param rows; see the cache-hit branch.)
		if (!debugShim) this.recordVariant(recipeIDs, prog, resolved, (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + (useVariantParams ? ":variantParams" : ""), opts);
		// Set7 (2026-08-25) — pre-link + keep the program alive so first chunk use doesn't stall
		// (see warmMaterialForProgram).  Set13 — the pre-link is ASYNC (`compileAsync`), and the
		// program is shielded while in flight (warmMaterialForProgram sets `prog.compiling` +
		// `prog.compiledPromise` synchronously HERE — before the `programs.set` below makes it
		// cache-visible).  getProgramForSet returns null for it until the link lands, and we AWAIT
		// the warm so this ensureProgram resolves only after the program is draw-ready.
		const warmP = this.warmMaterialForProgram(prog, opts);
		this.programs.set(setHash, prog);
		this.program = prog;
		this.setHash = setHash;
		await warmP;
		return prog;
	}

	/** Set6/7 — record the Layer-2 VARIANT for a recipe-identity set (its own ROW in the shared
	 *  variant-params texture, packed from the given resolved recipes' used-param defaults).
	 *  Idempotent: an already-recorded identity set is a no-op.  The variant references the
	 *  Layer-1 program (shared GL) but points at its own row.
	 *  @param opts The assembly opts (useSplat/useRegionParams/splatSize/regionParamsColumns/
	 *  useVariantParams/variantRowColumns) — the variant's layout must be assembled from THIS
	 *  identity set's resolved recipes with the SAME opts the Layer-1 program used (so the
	 *  variant-params cursor + the `u_p_<index>` refs align). */
	private recordVariant(recipeIDs: string[], program: TerrainMaterialProgram, resolved: readonly ResolvedRecipe[], flagKey: string, opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number} = {}): void {
		const key = this.setMemoKey(recipeIDs, flagKey);
		if (this.variants.has(key)) return;
		const {row, paramLayouts, values} = this.packVariantRow(resolved, opts);
		this.variants.set(key, {program, row, paramLayouts, values, recipeIDs, key});
	}

	/** Set6/7 — record the Layer-2 VARIANT for an identity set during warm-up, looking up the
	 *  Layer-1 program by its setHash (already stored by the first identity of that hash).
	 *  Guards against the program not yet existing (a race with the first-identity store) by
	 *  no-opping; the caller's own recordVariant after the store covers the first identity. */
	private recordVariantForSet(recipeIDs: string[], setHash: string, resolved: readonly ResolvedRecipe[], flagKey: string, opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number} = {}): void {
		const program = this.programs.get(setHash);
		if (program == null) return; // first identity stores the program just below; its recordVariant runs after
		this.recordVariant(recipeIDs, program, resolved, flagKey, opts);
	}

	/** P4 — fetch the assembled program for a specific recipe set (per-chunk program key),
	 *  WITHOUT making it the fast-path `this.program`.  Returns null when the set was never
	 *  ensured/warmed — the caller falls back to the fast-path program for that chunk.  A
	 *  set that is in the cache but structurally identical to another (D1 params-only free
	 *  variants) resolves to the SAME template — sharing the GL program + UBO.
	 *  Synchronous: the recipe→set-hash resolution is MEMOIZED at ensure/warm time (the async
	 *  resolve methods are the only ones that can import the DB accessors — this method must
	 *  not); a set never resolved through them is unknown here → null. */
	getProgramForSet(recipeIDs: string[], opts: {useSplat?: boolean; useRegionParams?: boolean; useVariantParams?: boolean} = {}): TerrainMaterialProgram | null {
		const useSplat = opts.useSplat === true;
		const useRegionParams = opts.useRegionParams === true && useSplat;
		const key = this.setMemoKey(recipeIDs, (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + ":variantParams");
		const setHash = this.resolvedSetHashByIds.get(key);
		if (setHash == null) return null;
		const prog = this.programs.get(setHash);
		if (prog == null) return null;
		// Set13 — the SHIELD: a program whose compileAsync is still in flight is not draw-ready.
		// Returning null routes the renderer to its SD3 hold-hidden branch (chunk stays invisible
		// + the deferred program-heal re-checks next frame), so a still-compiling program can
		// NEVER reach `renderObject -> setProgram -> getUniforms -> onFirstUse` — the call that
		// queries active uniforms and forces the driver to finish the compile synchronously.
		if (prog.compiling === true) return null;
		return prog;
	}

	/** Set6/7 — fetch the Layer-2 VARIANT for a specific recipe-identity set: the Layer-1
	 *  program + this identity set's ROW in the shared variant-params texture (packed from THIS
	 *  identity set's resolved defaults).  Distinct identity sets that share a Layer-1 structural
	 *  hash (Grass3 vs Grass_Jungle) therefore get DISTINCT ROWS (correct param values) while
	 *  sharing one GL program + one texture.  Returns null when the set was never ensured/warmed
	 *  (the caller falls back to row 0 / the program default).
	 *
	 *  Synchronous, like {@link getProgramForSet}: variants are built during the async ensure/warm
	 *  paths (where the identity set's recipes are resolved); this method is a pure cache lookup. */
	getVariantForSet(recipeIDs: string[], opts: {useSplat?: boolean; useRegionParams?: boolean; useVariantParams?: boolean} = {}): TerrainMaterialVariant | null {
		const useSplat = opts.useSplat === true;
		const useRegionParams = opts.useRegionParams === true && useSplat;
		const key = this.setMemoKey(recipeIDs, (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + ":variantParams");
		return this.variants.get(key) ?? null;
	}

	/** Set6→Set7 — build a Layer-2 VARIANT's ROW in the shared variant-params texture from a
	 *  specific identity set's RESOLVED recipes, by collecting THIS identity set's own used-param
	 *  layout (via {@link collectUsedParams} + {@link buildVariantParamLayouts} — the SAME order the
	 *  assembler's variant cursor walks) and packing it with the identity's resolved defaults.
	 *  This is the KEY correctness point for Issue A (Grass3-vs-grass): the Layer-1 program's UBO
	 *  values are bound to the PROGRAM's compile-time recipe ids (e.g. `grass/...`), so a chunk
	 *  whose identity set uses a DIFFERENT recipe id sharing a structural hash (Grass3) has NO
	 *  matching values — the program's values would silently be grass's.  Collecting + packing from
	 *  the chunk's own recipes gives the row offset configured with Grass3's values.
	 *
	 *  Correctness of the shared program: the GLSL is INDEX-based (`u_p_<setIndex>` / the variant
	 *  cursor — recipe ids never enter the GLSL), and two structurally-identical identity sets
	 *  produce IDENTICAL used-param ORDER → IDENTICAL layout alignment.  So the variant's row
	 *  (from the chunk's recipes) sits at the SAME indices the Layer-1 program's GLSL reads.
	 *
	 *  @returns The allocated row index + this identity's column layout + the packed Float32Array
	 *  row (already written into the manager's shared variant texture data). */
	private packVariantRow(resolvedRecipes: readonly ResolvedRecipe[], opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number}): {row: number; paramLayouts: VariantParamColumnLayout[]; values: Float32Array} {
		const usedParams = collectUsedParams(resolvedRecipes as ResolvedRecipe[]);
		// Row width: an EXPLICIT `variantRowColumns` is a fixed budget (bound-checked); absent,
		// the width is AUTO-COMPUTED to fit THIS set's used params — the same rule the assembler
		// uses (plan §2.1).  The shared texture grows to the widest row seen.  (The fixed 256
		// default was too small for the live starfield set's ~97 used uniforms.)
		const paramLayouts = opts.variantRowColumns != null
			? buildVariantParamLayouts(usedParams, opts.variantRowColumns)
			: buildVariantParamLayouts(usedParams);
		const floatWidth = opts.variantRowColumns != null
			? opts.variantRowColumns
			: Math.max(VARIANT_PARAMS_COLUMNS, Math.ceil(
				(paramLayouts.length == 0 ? 0 : paramLayouts[paramLayouts.length - 1].column + paramLayouts[paramLayouts.length - 1].span) / 4) * 4);
		const texture = this.ensureVariantTexture(floatWidth);
		// Guard 2 (§9.5) — reuse a reclaimed row before allocating a fresh one.  The freed row's
		// slot is guaranteed unused (its last chunk material was released before the row entered
		// the free-list), so overwriting it here is safe.
		const row = this.variantRowFreeList.length > 0 ? (this.variantRowFreeList.pop() as number) : this.nextVariantRow++;
		// Pack the row at the CURRENT texture width (ensureVariantTexture grew the texture +
		// row width to `floatWidth` if needed, so the row is exactly this wide).  Returns a
		// `variantRowFloatWidth`-float array (one row of RGBA texels — NO *4: the float width IS
		// the texel-row byte/float length).
		const values = packVariantParamsRow(paramLayouts, {}, this.variantRowFloatWidth);
		// Grow the data buffer for the new row if needed (ensureVariantTexture already sized the
		// texture's image; grow the backing Float32Array too).  Row stride = variantRowFloatWidth
		// floats (the DataTexture's texel-row length = widthTexels * 4) — NOT *4 again (the
		// 2026-08-25 stride bug put rows 4x apart → shader read zeros → black terrain).
		const needed = (row + 1) * this.variantRowFloatWidth;
		if (this.variantData == null || this.variantData.length < needed) {
			const grown = new Float32Array(Math.max(needed, (this.variantData?.length ?? 0) * 2));
			if (this.variantData) grown.set(this.variantData);
			this.variantData = grown;
		}
		this.variantData!.set(values, row * this.variantRowFloatWidth);
		this.syncVariantTextureHeight();
		texture.needsUpdate = true;
		return {row, paramLayouts, values};
	}

	/** Set7 — sync the variant texture's image height to the current row count + mark dirty
	 *  (keeps the DataTexture's allocation view consistent after row growth).  2026-08-26 —
	 *  when the dims/data actually change, install a FRESH Source so three.js re-allocates its
	 *  immutable texStorage2D at the new dims (a plain in-place image mutation + needsUpdate
	 *  issues texSubImage2D into the OLD allocation → GL_INVALID_VALUE overflow → the grown
	 *  rows never reach the GPU → pitch-black deep-zoom chunks). */
	private syncVariantTextureHeight(): void {
		const tex = this.variantTexture;
		if (tex == null || this.variantData == null) return;
		const widthTexels = Math.floor(this.variantRowFloatWidth / 4);
		const height = Math.max(this.nextVariantRow, 1);
		if (tex.image.width !== widthTexels || tex.image.height !== height || tex.image.data !== this.variantData) {
			tex.dispose();
			tex.image = {data: this.variantData, width: widthTexels, height};
			// Use a fresh source → sourceProperties.__version undefined → three re-allocates its immutable texStorage2D at the new dims.
			// (else the resize uploads into the stale smaller allocation and is rejected).
			tex.source = new THREE.Source(tex.image);
			tex.needsUpdate = true;
		}
	}

	/** Build the std140 param UBO for a set of layouts (scalar → 4B, vec4/vec4[6] → 16B-aligned —
	 *  mirrors computeParamLayouts; three computes offsets from the VALUE types).  Shared by the
	 *  live template + the three.js-layer warm-up scratch materials.
	 *
	 *  CRITICAL three.js UBO shape: an array param (ramp = `vec4 u_p_x[6]`) must be added as a
	 *  NESTED ARRAY of Uniforms — `paramGroup.add([u0, u1, ...])` — one Uniform per vec4 stop.
	 *  A single `new THREE.Uniform([vec4, vec4, ...])` makes `hasUniformChanged` call
	 *  `value.clone()` on the ARRAY (arrays have no .clone) → crash on first UBO upload. */
	buildParamGroup(paramLayouts: PackedParamLayout[]): THREE.UniformsGroup {
		const paramGroup = new THREE.UniformsGroup();
		paramGroup.setName("MaterialParams");
		for (const l of paramLayouts) {
			if (l.kind == "scalar") {
				paramGroup.add(new THREE.Uniform(l.defaultValue as number));
			} else if (l.kind == "color") {
				const c = l.defaultValue as [number, number, number];
				paramGroup.add(new THREE.Uniform(new THREE.Vector4(c[0] ?? 0, c[1] ?? 0, c[2] ?? 0, 0)));
			} else {
				// ramp → vec4[6]; one Uniform PER stop, nested-array entry (see docblock).
				const stops = (l.defaultValue as number[]) ?? [];
				const quads: THREE.Vector4[] = [];
				for (let i = 0; i < stops.length; i += 4) {
					quads.push(new THREE.Vector4(stops[i] ?? 0, stops[i + 1] ?? 0, stops[i + 2] ?? 0, stops[i + 3] ?? 0));
				}
				// Pad to exactly 6 stops (rampVal terminates at t = 1.0 repeats).
				const last = quads[quads.length - 1] ?? new THREE.Vector4(1, 0, 0, 0);
				while (quads.length < 6) quads.push(new THREE.Vector4(1, last.y, last.z, last.w));
				paramGroup.add(quads.map(q=>new THREE.Uniform(q)));
			}
		}
		return paramGroup;
	}

	/** Scratch ortho camera for the three.js-layer warm-up renders (never rendered for real).
	 *  Positioned at +z=1 looking at the origin, so the z=0 scratch plane sits inside the
	 *  near(0.1)..far(10) frustum — otherwise three frustum-culls the scratch and never compiles
	 *  its program (the "warm materials exist but no program is ever linked" bug, 2026-08-25). */
	private warmCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);

	/** Set7 (2026-08-25) — pre-link a set's three-layer program NOW (scratch render through the
	 *  attached three renderer) + keep the scratch material alive in `warmMaterials`.  Without
	 *  this, a program's first chunk use blocks on a synchronous link (WebGLPrograms.js `releaseProgram`
	 *  also DELETES a program when its last rendered material is disposed — so zoom-out eviction
	 *  drops it and zoom-in re-links from scratch). */
	private warmMaterialForProgram(
		prog: TerrainMaterialProgram,
		opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number},
	): Promise<void> {
		const three = this.threeRenderer;
		if (!three) return Promise.resolve(); // no renderer (worker/headless) — nothing to pre-link against
		this.noteCompileIssued();
		// Set13 — mark the program as still-compiling for the WHOLE duration of the warm, and
		// register the completion promise ON the program, BOTH synchronously (before any await
		// can yield).  This is the shield: while `compiling` is true, getProgramForSet returns
		// null (the renderer's SD3 hold-hidden path keeps the chunk invisible), and any
		// ensureProgram cache-hit on this program awaits `compiledPromise` — so NOTHING can hand
		// this program to a render before the link lands, regardless of which path dispatched
		// the warm (ensureProgram, warmUpPrograms, or the test-only re-warm).
		prog.compiling = true;
		const done = (async ()=>{
			try {
				await this.warmMaterialForProgramCore(prog, opts, three);
			} finally {
				prog.compiling = false;
				this.noteCompileResolved();
			}
		})();
		prog.compiledPromise = done;
		return done;
	}

	/** The actual compileAsync dispatch + scratch keep-alive for {@link warmMaterialForProgram}
	 *  (split out so the Set13 compile-counter bookkeeping wraps the await exactly once). */
	private async warmMaterialForProgramCore(
		prog: TerrainMaterialProgram,
		opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number},
		three: THREE.WebGLRenderer,
	): Promise<void> {
		const paramLayouts = prog.paramLayouts;
		// Share the live template's OWN paramGroup (prog.paramGroup) — NOT buildParamGroup again.
		// A fresh group per scratch would burn a SECOND UBO binding point per program (the Set7
		// plan §6 flagged "~2 binding points per variant" from exactly this: a scratch + a live
		// group).  Sharing one group per program keeps binding points ≈ program count (the plan's
		// §2.5 target).  The shared group is also what the live clones reference, so this scratch
		// and the live template agree on the MaterialParams block.
		const scratch = buildMaterialTemplate(prog.material.fragmentShader, opts.useSplat === true, paramLayouts, THREE, ()=>prog.paramGroup, opts.useRegionParams === true, opts.useVariantParams !== false);
		// Pre-link via three's purpose-built `compileAsync()` — the SAME getProgram→getProgramCacheKey
		// path a real render uses.  It never binds a target (so outputColorSpace/toneMapping come
		// from the renderer fields, NOT the srgb-linear/NoToneMapping an offscreen RT forces) and
		// never draws (no flash); materials from `scene`, lights from `targetScene`.  Unlike the
		// old synchronous `compile()`, `compileAsync` issues the compile through
		// KHR_parallel_shader_compile (when available) and RESOLVES once the program is linked —
		// so the main thread never blocks on the driver (Set13 — async shader compilation).  On
		// browsers without the extension three falls back to its synchronous path (decision-2's
		// "sync fallback, same as today") with no API change on our side.
		const scratchScene = new THREE.Scene();
		scratchScene.add(new THREE.Mesh(new THREE.PlaneGeometry(1, 1), scratch));
		const camera = this.liveCamera ?? this.warmCamera;
		// Pin the LIVE output config (toneMapping + outputColorSpace).  With no render target
		// bound, these renderer fields ARE folded into the program cacheKey (the live values are
		// set per-frame by ToneMappingController, so the renderer defaults would differ).
		const prevToneMapping = three.toneMapping;
		const prevOutputColorSpace = three.outputColorSpace;
		if (this.outputConfig) {
			const cfg = this.outputConfig();
			three.toneMapping = cfg.toneMapping as THREE.ToneMapping;
			three.outputColorSpace = cfg.outputColorSpace as THREE.ColorSpace;
		}
		try {
			// Materials from scratchScene, lights from the LIVE scene (targetScene) so the
			// compiled program's cacheKey folds the SAME light/shadow counts as the live render.
			await three.compileAsync(scratchScene, camera, this.liveScene ?? scratchScene);
			// Set13 — LINK_STATUS probe (the "what did we lose by dropping the raw layer"
			// mitigation): post-completion, read LINK_STATUS ONCE (cheap — the compile has
			// finished, so this does not force a synchronous stall).  three logs its own error;
			// our probe adds the labelled ERROR the renderer's fallback logic can react to.
			this.probeProgramLinkStatus(prog.setHash);
		} finally {
			three.toneMapping = prevToneMapping;
			three.outputColorSpace = prevOutputColorSpace;
		}

		// Retire any prior scratch for this hash (its program may be destroyed — the new scratch
		// re-warms it; the live template uses whichever comes last).
		const prev = this.warmMaterials.get(prog.setHash);
		if (prev && prev.material !== scratch) prev.material.dispose();
		this.warmMaterials.set(prog.setHash, {material: scratch, setHash: prog.setHash});
	}

	/** Set13 — post-async-compile LINK_STATUS probe.  After a `compileAsync` resolves, read the
	 *  linked program's LINK_STATUS ONCE (cheap post-completion) and console.error a labelled
	 *  message on failure.  This restores the exception-based error surfacing we lose by dropping
	 *  the raw compile layer (three logs its own info-log via `checkShaderErrors`, but as a
	 *  console message — not something the app's fallback logic can catch).  The renderer reacts
	 *  to the labelled error by falling back to the legacy bake path. */
	private probeProgramLinkStatus(setHash: string): void {
		const renderer = this.threeRenderer;
		const gl = this.gl;
		if (!renderer || !gl) return;
		try {
			// The template material's three-layer program (the one `compileAsync` just linked),
			// via three's public `renderer.properties`.  currentProgram is only set once the
			// material has actually been through a program acquire; if it's absent, the compile
			// didn't reach link (nothing to probe) — three's onShaderError already covered it.
			const prog = this.programs.get(setHash);
			const mat = prog?.material;
			if (!mat) return;
			const materialProps = renderer.properties.get(mat) as {currentProgram?: {program: WebGLProgram}} | undefined;
			const glProgram = materialProps?.currentProgram?.program;
			if (!glProgram) return;
			if (gl.getProgramParameter(glProgram, gl.LINK_STATUS) !== true) {
				console.error(`[TerrainMaterialManager] three-layer program link FAILED for set ${setHash} — falling back to the legacy bake path.`);
			}
		} catch (err) {
			// Probe is best-effort — never let the (post-compile) bookkeeping mask a real render.
			console.error("[TerrainMaterialManager] LINK_STATUS probe failed", err);
		}
	}

	/** Set13 — record a three-layer compile ISSUE (a `compileAsync` dispatched).  Bumps the
	 *  in-flight counter + the wave's total, and mirrors both to `terrainProcessStats` so the
	 *  MenuBar's "Compiling shaders (X/Y)" label updates (the store is observable).  When the
	 *  counter was at 0 (nothing compiling), this ISSUE starts a NEW wave → the total is reset
	 *  to 0 first so `materialCompilesTotal` counts exactly THIS wave's compiles (the Y in X/Y),
	 *  not a lifetime accumulation across overlapping waves.
	 *
	 *  `terrainProcessStats` is imported lazily so the BROWSER GPU test lane (which imports this
	 *  file for `buildMaterialTemplate`/`compileRawFragmentProgram`) never transforms
	 *  TerrainProcessStats.ts — its `@O accessor` decorators break that lane's esbuild.  The live
	 *  render path calls these methods on real compiles, where the dynamic import is a cached
	 *  no-op after the first call. */
	private noteCompileIssued(): void {
		if (this.materialCompilesPending == 0) this.materialCompilesTotal = 0; // new wave
		this.materialCompilesPending++;
		this.materialCompilesTotal++;
		void import("../Heights/TerrainProcessStats.js").then(({terrainProcessStats})=>{
			// Wrapped in an action (MobX strict-mode): writes to the observable store must go
			// through an action, else strict-mode throws on every counter update.
			RunInAction_Set(()=>{
				terrainProcessStats.materialCompilesPending = this.materialCompilesPending;
				terrainProcessStats.materialCompilesTotal = this.materialCompilesTotal;
			});
		});
	}

	/** Set13 — record a three-layer compile RESOLUTION (a `compileAsync` fulfilled/rejected).
	 *  Decrements the in-flight counter; when the wave drains back to 0, nulls the pending field
	 *  so the progress label disappears (the total is left intact for a trailing read). */
	private noteCompileResolved(): void {
		this.materialCompilesPending = Math.max(0, this.materialCompilesPending - 1);
		void import("../Heights/TerrainProcessStats.js").then(({terrainProcessStats})=>{
			RunInAction_Set(()=>{
				if (this.materialCompilesPending <= 0) {
					terrainProcessStats.materialCompilesPending = null;
				} else {
					terrainProcessStats.materialCompilesPending = this.materialCompilesPending;
				}
			});
		});
	}

	/** TEMP DEBUG (2026-08-25) — re-warm a set's program on demand so the console can test
	 *  whether re-rendering the scratch revives a lost three-layer program. */
	__testOnly_reWarm(setHash: string): void {
		const prog = this.programs.get(setHash);
		if (!prog) { console.log("[rewarm] no program for", setHash); return; }
		this.warmMaterialForProgram(prog, {useSplat: true, useRegionParams: true, useVariantParams: true}).then(()=>{
			console.log("[rewarm] done for", setHash);
		});
	}

	/** Phase 6 — program-cache warm-up: compile-ahead the material-set programs reachable from
	 *  the active splat recipe ids, so panning into a new material region never pays a shader
	 *  compile (the plan's explicit D1 cost).  Idempotent + cheap: sets already in the module
	 *  program cache are skipped, and the enumeration DEDUPES BY SET HASH (D10 — params-only
	 *  variants collapse; with hundreds of user variants the warm-up set is usually far smaller
	 *  than the recipe count).
	 *
	 *  Reachable sets = every PREFIX of the distinct recipe list (slot 0 = the fallback is always
	 *  present; a chunk's per-chunk recipeIDs is exactly a prefix of this list — see
	 *  SplatRasterizer.collectRecipeIDs + the renderer's splatRecipeIDs).  A chunk far from any
	 *  boundary uses only the fallback (1-way); a chunk straddling the fallback + first boundary
	 *  needs the 2-way shader; etc.  A chunk never mixes a NON-prefix subset (the slot order is
	 *  assignment order, and a lower-indexed material appearing implies all lower indices appear
	 *  somewhere in the chunk's sample set) — so prefix enumeration is exact.
	 *
	 *  Runs OUTSIDE the frame loop (the renderer schedules it during idle).  Warms TWO layers:
	 *  (1) the module-scoped program cache the live ensureProgram consults (the raw fragment
	 *  pre-compile — validates the GLSL + makes the module cache a hit), and (2) three.js's own
	 *  WebGLProgram cache via scratch ShaderMaterials rendered once through the attached renderer
	 *  (the layer that actually renders — see below).  A warm set is a cache HIT at first
	 *  appearance in BOTH layers.
	 *
	 *  three.js's program cache CAN be pre-warmed — but only by compiling the SAME fragment
	 *  string the live template uses through the SAME renderer: its cacheKey folds
	 *  customFragmentShaderID (a per-fragmentShader-STRING id from WebGLShaderCache) + uniform
	 *  booleans + customProgramCacheKey (onBeforeCompile.toString() — same for all our
	 *  materials) + the scene's light counts (ambient lights don't count).  A scratch
	 *  ShaderMaterial built identically to the live template (same fragment, same uniform
	 *  decls) therefore lands on the SAME cacheKey.  The scratch must be KEPT ALIVE until the
	 *  live template appears — three destroys a program when its last referencing material is
	 *  GC'd (usedTimes → 0), so the warm-up holds the scratches in `warmMaterials`.
	 *
	 *  @param distinctSets P4 — the DISTINCT per-chunk recipe sets that actually occur,
	 *  enumerated by the renderer from region bbox ↔ chunk overlap
	 *  ({@link SplatRasterizer.enumerateChunkRecipeSets}, plan §4.3 A).  The old prefix-of-one-
	 *  global-list enumeration is GONE: per-chunk sets are not prefixes of a single list, so
	 *  warm-up must compile exactly the sets chunks will request.  Each set is compiled in
	 *  BOTH layers + recorded in the manager's per-set program cache, so the frame loop's
	 *  per-chunk program fetch is a HIT at first appearance.  (A set also present in the
	 *  renderer's ensureProgram calls gets re-ensured there — a no-op, same set hash.)
	 *  @param opts.useSplat Whether the reachable programs are splat shaders (default true —
	 *  the splat path is the only multi-set path today).  `opts.splatSize` is threaded through
	 *  (a compile-time constant — must match the rasterised texture, plan §12.1).
	 *  @returns The set hashes actually compiled (empty when everything was already cached). */
	async warmUpPrograms(
		distinctSets: string[][],
		opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number} = {},
	): Promise<string[]> {
		const gl = this.gl;
		if (!gl) throw new Error("[TerrainMaterialManager] no WebGL context attached");
		// 2026-08-25 — DEBUG SHIM: the shim collapses every recipe set to ONE structural hash, so
		// warm-up is a no-op — there is exactly one program (ensured by the renderer's
		// ensureProgram; a resident single program needs no warm-ahead of other sets).
		if (this.__debugShim) return [];
		const useSplat = opts.useSplat !== false; // splat is the only multi-set path today
		const useRegionParams = opts.useRegionParams === true && useSplat;
		const useVariantParams = opts.useVariantParams !== false && useSplat;
		const flagKey = (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + (useVariantParams ? ":variantParams" : "");

		const compiled: string[] = [];
		// Set13 — FIRE-ALL (decision 4): collect every set's async scratch-warm promise and await
		// them together after the loop, so all driver compile requests are issued at once and the
		// driver queues them (that's what KHR_parallel_shader_compile is for).  The synchronous
		// per-set program-cache population (below) still happens in-loop, so getProgramForSet is
		// correct the moment this method starts compiling; only the LINK completion is deferred.
		const warmPromises: Promise<void>[] = [];
		// Resolve each set's ids (per-id memo makes repeat resolutions free).  Dedupe by set
		// hash: params-only variants + id-aliased recipes collapse to one program (D1/D10 — the
		// whole point of the warm-up being cheap).
		const seen = new Set<string>();
		const {ResolveRecipeSet} = await import("../../Server/DB/mapObjects/ProcMatAccessors.js");
		for (const ids of distinctSets) {
			// Set9 P3 (Part F) — the id list is a SLOT-ORDER SEQUENCE that can repeat a recipe
			// id (an F-split run occupies two slots: e.g. `[starfield, GJ, grass, GJ]`).  Drop
			// only EMPTY ids (slot 0 = the base, never empty) — preserve duplicates + position,
			// because the set hash is the order-sensitive structural-hash SEQUENCE and the
			// variant row is keyed positionally (a de-duplicated list would silently collapse an
			// F-split set onto the wrong program/row).  Mirrors SplatRasterizer.assignRegionSlots'
			// slot-ordered output.
			const deduped: string[] = [];
			for (const id of ids) {
				if (id != "") deduped.push(id);
			}
			if (deduped.length == 0) continue;
			if (useSplat && deduped.length > SPLAT_MAX_MATERIALS) {
				// Warm-up is best-effort: an over-cap set is a data error that the rasterizer +
				// the live ensureProgram both reject; skip rather than fail the whole pass.
				continue;
			}
			const resolved = ResolveRecipeSet(deduped, this.mapID);
			// The MANAGER-format set hash — the SAME construction ensureProgram uses (structural
			// hashes sorted + the splat/regionParams flags) — so the warmed program lands in the
			// same cache the live per-chunk fetch (getProgramForSet) reads.
			const setHash = resolved.map(r=>r.structuralHash).join("~") + flagKey;
			// Record the id-list → set-hash memo so the sync per-chunk program fetch
			// (getProgramForSet) can resolve THIS set without re-importing the accessors.
			this.resolvedSetHashByIds.set(this.setMemoKey(deduped, flagKey), setHash);
			// Set6 — a SECOND identity set sharing a setHash (structurally-identical distinct
			// recipes — the Grass3/Jungle case) is deduped below, but it still needs its OWN
			// Layer-2 variant (its own UBO values).  Record it here BEFORE the dedup, reusing the
			// already-created program entry (its layouts match — identical structure).  The FIRST
			// identity's variant is recorded after the program store below.
			if (seen.has(setHash)) {
				this.recordVariantForSet(deduped, setHash, resolved, flagKey, opts);
				continue;
			}
			seen.add(setHash);
			// Assemble with the SAME opts as the live ensureProgram path (lit + splat + region
			// params) — the three-layer program cacheKey is content-based, so a plain/no-lighting
			// assembly here would pre-warm a DIFFERENT program than the one the live path renders.
			const assembled = assembleMaterialShader(resolved, {includeLighting: true, useSplat, splatSize: opts.splatSize, useRegionParams, regionParamsColumns: opts.regionParamsColumns, useVariantParams, variantRowColumns: opts.variantRowColumns, roughnessAwareSpecular: true, terrainRegionClip: true, outputTransfer: true});

			// Set13 — the module-layer raw pre-compile (compileMaterialProgram) is GONE from the
			// live path (it was the second synchronous GL compile that blocked the main thread).
			// GLSL validation for recipes lives in the GPU test lanes; the three-layer scratch
			// below (now `compileAsync`) is the only live compile, and error surfacing comes from
			// onShaderError + the LINK_STATUS probe.
			// P4 — store the assembled template in the manager's per-set program cache so the
			// sync per-chunk fetch (getProgramForSet) returns it at first appearance (the warm-up
			// compiles the three.js layer via the scratch below — this entry is the live template
			// the frame loop clones chunks from; if the SAME set is later ensureProgram'd, that
			// path reuses this entry (no re-assemble/re-compile).
			let prog: TerrainMaterialProgram | null = null;
			if (!this.programs.has(setHash)) {
				const paramLayouts = assembled.paramLayouts;
				const paramGroup = this.buildParamGroup(paramLayouts);
				const material = buildMaterialTemplate(assembled.glsl, useSplat, paramLayouts, THREE, ()=>paramGroup, useRegionParams, useVariantParams);
				prog = {material, paramGroup, paramLayouts, setHash, recipe: resolved[0], recipeIDs: deduped};
				this.programs.set(setHash, prog);
			}
			// Set6 — record this identity set's Layer-2 variant (its own UBO packed from these
			// resolved recipes).  Uses the just-stored program entry's layouts.
			this.recordVariantForSet(deduped, setHash, resolved, flagKey, opts);

			// 2) three.js-layer pre-compile + keep-alive: render a scratch ShaderMaterial (SAME
			// fragment string the live template uses) once through the attached three renderer,
			// populating three's WebGLProgram cache AND keeping the program resident in
			// `warmMaterials` (a three program is destroyed when its last rendered material is
			// GC'd — usedTimes → 0 — so holding the scratch material keeps the program alive
			// across chunk evictions; see warmMaterialForProgram).  Set13 — the scratch warm is
			// `compileAsync`-backed.  FIRE-ALL: the promise is collected, not awaited in-loop, so
			// the driver receives every set's compile request before any of them resolve.
			if (prog) warmPromises.push(this.warmMaterialForProgram(prog, opts));
			compiled.push(assembled.structuralHash);
		}
		// Set13 — wait for the fire-all compiles to LINK before resolving this method's promise
		// (resolution IS completion — see the class docblock's note on compileAsync semantics).
		await Promise.all(warmPromises);
		return compiled;
	}

	/** Clone the template material with a fresh per-chunk uniforms object (sharing the UBO).
	 *  Returns a material ready for `mesh.material` — set the per-chunk uniforms via
	 *  {@link setChunkUniforms} before the next render.  Set7: the clone starts at row 0 (the
	 *  all-defaults fallback) — the renderer sets the per-chunk variant row via
	 *  {@link setChunkVariantRow}. */
	/** 2026-08-26 — DEBUG SHIM: give a freshly-cloned material its OWN u_debugShimColors
	 *  Vector4 array.  three's UniformsUtils.cloneUniforms copy-constructs plain arrays
	 *  (`value.slice()`), so the vector ELEMENTS stay shared across clones — but under the shim
	 *  each chunk's setChunkSplatDist REWRITES the palette per chunk (the per-chunk slot order),
	 *  which would stomp every other chunk's palette through those shared elements.
	 *  No-op when the uniform is absent (non-shim materials). */
	private static GiveOwnShimPalette(mat: THREE.ShaderMaterial): void {
		const u = mat.uniforms?.u_debugShimColors as {value: THREE.Vector4[]}|undefined;
		if (!u) return;
		u.value = u.value.map(v=>v.clone());
	}

	cloneChunkMaterial(): THREE.ShaderMaterial {
		if (!this.program) throw new Error("[TerrainMaterialManager] ensureProgram must run before cloneChunkMaterial");
		return this.cloneChunkMaterialForProgram(this.program);
	}

	/** P4 — clone a material from a SPECIFIC program (per-chunk program key), not just the
	 *  fast-path program.  Used by the frame loop when a chunk's recipe set differs from the
	 *  last-ensured set — the chunk's cloned material carries its own set's template + UBO.
	 *  @throws when the set has no assembled program (the renderer must fall back BEFORE
	 *  calling — {@link getProgramForSet} first). */
	cloneChunkMaterialForProgram(prog: TerrainMaterialProgram): THREE.ShaderMaterial {
		const src = prog.material;
		const mat = src.clone() as THREE.ShaderMaterial;
		mat.uniforms = THREE.UniformsUtils.clone(src.uniforms);
		mat.uniformsGroups = src.uniformsGroups;
		TerrainMaterialManager.GiveOwnShimPalette(mat);
		return mat;
	}

	/** Set7 — bind the shared variant-params texture + set a chunk's VARIANT ROW (a plain `int`
	 *  uniform — the per-material upload that Fixes Finding A; §2.2).  The row selects which
	 *  recipe-identity set's param values the shader reads.  Binds the shared texture on the
	 *  material (cheap; all materials share it). */
	setChunkVariantRow(mat: THREE.ShaderMaterial, row: number): void {
		const ut = mat.uniforms.u_variantParams;
		if (ut) (ut.value as THREE.Texture) = this.getVariantTexture();
		const ur = mat.uniforms.u_variantRow;
		if (ur) (ur.value as number) = row;
	}

	/** Guard 2 (Set7 §9.5) — mark a variant as referenced by a live chunk (increment its refcount
	 *  so its row is not evicted).  Called by the renderer when a chunk binds a variant row.  The
	 *  variant key is the identity-set memo key (setMemoKey) — the same key `getVariantForSet`
	 *  uses, so the renderer can pass the set's ids + flags directly. */
	acquireVariantRow(key: string): void {
		this.variantRowRefs.set(key, (this.variantRowRefs.get(key) ?? 0) + 1);
	}

	/** Guard 2 (§9.5) — decrement a variant's refcount (a live chunk stopped holding this
	 *  identity set's row).  The renderer calls this from the chunk-EVICTION lifecycle AFTER the
	 *  chunk material (holding the `u_variantRow` value) is released.
	 *
	 *  IMPORTANT (2026-08-25 fix): on refcount 0 we do NOT delete the `variants` entry or reclaim
	 *  its row.  A chunk that reappears after a zoom (or pan out-and-back) must find its row +
	 *  packed values INTACT via `getVariantForSet` — deleting the entry here meant the only
	 *  re-record paths (`ensureProgram`/`warmUpPrograms`) had to fire, and warm-up is gated on
	 *  `regionsSignature` (unchanged by a zoom), so nothing re-recorded → every reappearing chunk
	 *  fell back to the all-zeros row 0 → black terrain.  Keeping the entry means a reappearing
	 *  chunk reads its correct row with no re-record needed.  Row growth is bounded by the number
	 *  of DISTINCT identity sets ever seen (≤ live chunk sets in practice — plan §10.8), so we
	 *  trade the old free-list reuse for guaranteed correctness on zoom.  (`releaseAllVariants`
	 *  still bulk-clears on a region-signature rebuild, where every set legitimately changes.) */
	releaseVariantRow(key: string): void {
		const prev = this.variantRowRefs.get(key) ?? 0;
		if (prev <= 0) return; // not acquired (or already released) — no-op
		const next = prev - 1;
		if (next <= 0) {
			// Keep the `variants` entry (row + values) so a reappearing chunk renders correctly;
			// only drop the refcount bookkeeping.  Do NOT push the row to the free-list — reusing
			// it for another set's data would corrupt this kept entry's values.
			this.variantRowRefs.delete(key);
		} else {
			this.variantRowRefs.set(key, next);
		}
	}

	/** Set the per-chunk uniforms on a cloned material.  `albedo` = the hypsometric-ramp colour
	 *  (0..255, SUGGESTED AS sRGB — the color-picker / DEFAULT_HYPSOMETRIC_STOPS are sRGB display
	 *  colors).  The material's base albedo input (plan §8) is LINEAR: sRGB → linear via three's
	 *  `SRGBToLinear` (the SAME transfer the bake's sRGB-tagged colour maps get when
	 *  MeshStandardMaterial samples them), so terrain and tree albedo sit on one curve.
	 *  The per-sample geometric normal lattice is set separately via {@link setChunkGeoNormal}. */
	setChunkUniforms(mat: THREE.ShaderMaterial, albedo: [number, number, number]): void {
		const u = mat.uniforms;
		const r = SRGBToLinear(albedo[0] / 255), g = SRGBToLinear(albedo[1] / 255), b = SRGBToLinear(albedo[2] / 255);
		(u.u_baseAlbedo.value as THREE.Vector3).set(r, g, b);
	}

	/** Set the per-chunk geometric-normal lattice texture (the worker's 33×33 normals array,
	 *  slope_scale ALREADY applied, z-up — the same per-sample normals the old CPU bake used
	 *  per texel).  The shader's matGeoNormal() fetches it per fragment, giving hillshade +
	 *  mask-slope coverage fine-cell granularity (not the Phase-3 chunk-uniform centre sample). */
	setChunkGeoNormal(mat: THREE.ShaderMaterial, tex: THREE.DataTexture): void {
		(mat.uniforms.u_geoNormal.value as THREE.Texture) = tex;
	}

	/** Set the per-chunk HEIGHT lattice texture (2026-08-13, water elevation-reactive colour) —
	 *  the worker's CHUNK_N×CHUNK_N heights, R32F, the same wiring as {@link setChunkGeoNormal}.
	 *  The shader's `matGeoHeight()` fetches it per fragment; the `geoHeight` primitive maps it
	 *  to a depth/current colour ramp. */
	setChunkGeoHeight(mat: THREE.ShaderMaterial, tex: THREE.DataTexture): void {
		(mat.uniforms.u_geoHeight.value as THREE.Texture) = tex;
	}

	/** Set the per-chunk D-LESS height lattice texture (Set15 P3 §3.4 — the VERTEX-displacement
	 *  input `u_geoHeightGeom`, R32F).  Same shape/wiring as {@link setChunkGeoHeight}, but the
	 *  field EXCLUDES the ambient detail band D (the fragment renders D at pixel scale, so
	 *  displacing by the D-inclusive `u_geoHeight` would double-count it).  Only consumed by the
	 *  vertex shader (via `texelFetch`); harmless when `u_displaceScale` is 0 (2D). */
	setChunkGeoHeightGeom(mat: THREE.ShaderMaterial, tex: THREE.DataTexture): void {
		(mat.uniforms.u_geoHeightGeom.value as THREE.Texture) = tex;
	}

	/** Set15 P6 (§3.7) — per-chunk edge-stitch COARSENESS LEVELS (`u_edgeCoarserLevel`,
	 *  [south, east, north, west] order: 0 = same/finer neighbour, k = the neighbour is k levels
	 *  coarser).  Set per 3D chunk from the quadtree's `edgeCoarserLevels`; the vertex shader
	 *  stitches a border vertex by lerping between the two nearest coarse samples — which are this
	 *  chunk's OWN lattice samples at `2^k`-spaced indices (the quadtree is aligned) — so ANY
	 *  power-of-two level ratio is handled with no neighbour data and no 2:1-balance requirement.
	 *  Harmless when `u_edgeStitchEnabled` is 0 (2D) — the stitch branch is gated off. */
	setChunkEdgeCoarser(mat: THREE.ShaderMaterial, levels: readonly [number, number, number, number]): void {
		(mat.uniforms.u_edgeCoarserLevel.value as THREE.Vector4).set(levels[0], levels[1], levels[2], levels[3]);
	}

	/** Set15 P8 (§6.2) — per-chunk SHADOW uniforms from the module-global shadow light. 2D passes
	 *  enabled 0 (getShadowMask → 1.0, byte-identical). Binds the light's depth texture + its
	 *  texture-matrix + map size + bias when enabled. `texture` null on the first frame → enabled 0. */
	setShadowUniforms(
		mat: THREE.ShaderMaterial,
		shadow: {
			light: THREE.DirectionalLight | null;
			mapSize: number;
		} | null,
	): void {
		const u = mat.uniforms;
		const light = shadow?.light ?? null;
		const mapTex = light?.shadow?.map?.texture ?? null;
		const enabled = (light != null && mapTex != null) ? 1 : 0;
		(u.u_shadowEnabled.value as number) = enabled;
		if (enabled && light != null && mapTex != null) {
			(u.u_shadowMap.value as THREE.Texture) = mapTex;
			(u.u_shadowMatrix.value as THREE.Matrix4).copy(light.shadow.matrix);
			(u.u_shadowMapSize.value as THREE.Vector2).set(light.shadow.mapSize.x, light.shadow.mapSize.y);
			(u.u_shadowBias.value as number) = light.shadow.bias;
		} else {
			// Leave u_shadowMap as whatever it was (null or a stale texture) — never sampled when
			// disabled, so no need to null it (avoiding churn).
			(u.u_shadowMatrix.value as THREE.Matrix4).identity();
			// NEGATIVE (see the template default above) — the compare is `step(coord.z + bias, depth)`,
			// so positive = more acne. Unused while disabled, but must not model the wrong sign.
			(u.u_shadowBias.value as number) = -0.0015;
		}
	}

	/** Set the per-chunk ambient-weight mask texture (issue 2, 2026-08-08) — the worker's
	 *  multiplier lattice (R32F); enables the mask fetch so ∇D is scaled by it.  Pass null
	 *  (or don't call) when no multiplier ring exists → flag stays 0, no-op. */
	setChunkAmbientWeight(mat: THREE.ShaderMaterial, tex: THREE.DataTexture | null): void {
		const u = mat.uniforms;
		if (tex) {
			(u.u_ambientWeightMask.value as THREE.Texture) = tex;
			(u.u_ambientWeightMaskEnabled.value as number) = 1;
		} else {
			(u.u_ambientWeightMaskEnabled.value as number) = 0;
		}
	}

	/** Set the set-wide frame uniforms on a material (per-frame values shared across chunks:
	 *  meters-per-pixel, light rig, opacity).  Call each frame before render.  `strength` here is
	 *  the SUN intensity (TerrainHeightShadingConfig.strength → DirectionalLight.intensity — the
	 *  Set14 P9 three-parity model), NOT the old 0..1 hillshade blend.
	 *  `displaceScale` (Set15 P3, D4): 0 in 2D (flat quads — byte-identical), 1 in 3D (displaced
	 *  by `u_geoHeightGeom`).  `cameraPos`: the camera's world position, used for the REAL view
	 *  vector in specular (D10). */
	setFrameUniforms(mat: THREE.ShaderMaterial, metersPerPixel: number, lightDir: [number, number, number], ambient: number, strength: number, slopeScale: number, opacity: number, displaceScale = 0, cameraPos: [number, number, number] | null = null): void {
		const u = mat.uniforms;
		(u.u_metersPerPixel.value as number) = metersPerPixel;
		(u.u_lightDir.value as THREE.Vector3).set(lightDir[0], lightDir[1], lightDir[2]);
		(u.u_ambient.value as number) = ambient;
		(u.u_sunIntensity.value as number) = strength;
		(u.u_slopeScale.value as number) = slopeScale;
		(u.u_opacity.value as number) = opacity;
		(u.u_displaceScale.value as number) = displaceScale;
		// Set15 P6 (D7) — the edge stitch tracks the 3D gate (`u_displaceScale`): 1 in 3D
		// (stitch active, skirt off), 0 in 2D (pre-P6 skirt behavior byte-identical).
		(u.u_edgeStitchEnabled.value as number) = displaceScale;
		if (cameraPos) (u.u_cameraPos.value as THREE.Vector3).set(cameraPos[0], cameraPos[1], cameraPos[2]);
	}

	/** Set the Set10 per-config ambient-detail uniform values (the residual band's fractal
	 *  parameters — see {@link ambientDetailUniformValues}).  Call on shading-config change
	 *  alongside the light rig (NOT per chunk; u_ambCellSize is set per chunk via setChunkSize).
	 *  `cellSize` = the chunk's height-lattice spacing (chunkSize / (CHUNK_N - 1)) — a default
	 *  used only when a chunk's size hasn't been set yet; setChunkSize overrides it per chunk. */
	setAmbientDetailUniforms(mat: THREE.ShaderMaterial, values: ReturnType<typeof ambientDetailUniformValues>, cellSize = 1): void {
		const u = mat.uniforms;
		(u.u_ambWavelength.value as number) = values.wavelength;
		(u.u_ambLacunarity.value as number) = values.lacunarity;
		(u.u_ambPersistence.value as number) = values.persistence;
		(u.u_ambAmplitude.value as number) = values.amplitude;
		(u.u_ambDetailStrength.value as number) = values.detailStrength;
		(u.u_ambOctaves.value as number) = values.octaves;
		(u.u_ambDetailOctaves.value as number) = values.detailOctaves;
		(u.u_ambFractalType.value as number) = values.fractalType;
		(u.u_ambOffset.value as THREE.Vector2).set(values.offset[0], values.offset[1]);
		(u.u_ambSeed.value as number) = values.seed;
		(u.u_ambCellSize.value as number) = cellSize;
	}

	/** Set the per-chunk splat textures (Phase 5): `indexTex` RGBA8UI (one index/sample) +
	 *  `distTex`/`distTex2` RGBA32F (slots 0–3 + slots 4–7 per-slot SIGNED DISTANCES in metres —
	 *  the P3 shader's u_splatDist/u_splatDist2; LINEAR-filtered). Built by buildSplatTextures;
	 *  dispose all on chunk eviction.  P5 — also sets the per-slot EFFECTIVE DOMINANCE uniforms
	 *  (the smin soft-union's gate, plan §4.2 C) from the chunk's `slotDominance`; absent → the
	 *  DISTINCT default (P3 ordered paint-over — backward compat).  Set7 §9 — dominance is
	 *  carried in two vec4s (u_slotDominanceA slots 0–3, u_slotDominanceB slots 4–7).
	 *  2026-08-26 — DEBUG SHIM: `chunkRecipeIDs` (the chunk's OWN slot order, slot i's recipe id)
	 *  rewrites the chunk clone's u_debugShimColors so slot i carries the colour of the recipe at
	 *  slot i FOR THIS CHUNK. The shim's program is shared map-wide but each chunk's splat indices
	 *  index its own per-chunk set — without this, the same raster index N means different
	 *  materials on chunks whose sets differ.  No-op when the ids are absent. */
	setChunkSplatDist(mat: THREE.ShaderMaterial, indexTex: THREE.DataTexture, distTex: THREE.DataTexture, distTex2: THREE.DataTexture, slotDominance?: number[], slotFeather?: number[], slotSoftUnion?: number[], slotNoiseAmp?: number[], slotNoiseScale?: number[], chunkRecipeIDs?: string[]): void {
		(mat.uniforms.u_splatIndex.value as THREE.Texture) = indexTex;
		(mat.uniforms.u_splatDist.value as THREE.Texture) = distTex;
		if (mat.uniforms.u_splatDist2) (mat.uniforms.u_splatDist2.value as THREE.Texture) = distTex2;
		if (this.__debugShim && chunkRecipeIDs && mat.uniforms.u_debugShimColors) {
			const cols = (mat.uniforms.u_debugShimColors as {value: THREE.Vector4[]}).value;
			for (let i = 0; i < SPLAT_MAX_MATERIALS; i++) {
				const id = chunkRecipeIDs[i];
				if (!cols[i]) cols[i] = new THREE.Vector4(0, 0, 0, 0);
				if (id != null) {
					const h = hashRecipe(id);
					const fr = (x: number): number => x - Math.floor(x);
					cols[i].set(
						0.25 + 0.75 * fr(h * 0.6180339887498949),
						0.30 + 0.70 * fr(h * 1.6180339887498949),
						0.35 + 0.65 * fr(h * 2.6180339887498949),
						h,
					);
				} else {
					cols[i].set(0, 0, 0, 0); // unused slot → shader's neutral fallback hue
				}
			}
		}
		const vA = mat.uniforms.u_slotDominanceA.value as THREE.Vector4;
		const vB = mat.uniforms.u_slotDominanceB.value as THREE.Vector4;
		if (slotDominance && slotDominance.length >= SPLAT_MAX_MATERIALS) {
			// Slots 0–3 in A, slots 4–7 in B.
			vA.set(slotDominance[0] ?? 0, slotDominance[1] ?? 0, slotDominance[2] ?? 0, slotDominance[3] ?? 0);
			vB.set(slotDominance[4] ?? 0, slotDominance[5] ?? 0, slotDominance[6] ?? 0, slotDominance[7] ?? 0);
		} else {
			// No per-chunk dominance → every region slot distinct → the P3 ordered paint-over.
			vA.set(0, 1, 2, 3);
			vB.set(4, 5, 6, 7);
		}
		// Set12 followup — per-slot RESOLVED FEATHER (the "fade-to-wrong-material" fix): each slot
		// is feathered with its OWN `feather + edgeNoise.amplitude` (the clamp its distance was
		// stored with), not the winning region's single feather.  Absent → leave the neutral 2.0
		// default (backward compat with pre-fix chunks).
		if (slotFeather && slotFeather.length >= SPLAT_MAX_MATERIALS && mat.uniforms.u_slotFeatherA) {
			const fA = mat.uniforms.u_slotFeatherA.value as THREE.Vector4;
			const fB = mat.uniforms.u_slotFeatherB.value as THREE.Vector4;
			fA.set(slotFeather[0] ?? 2, slotFeather[1] ?? 2, slotFeather[2] ?? 2, slotFeather[3] ?? 2);
			fB.set(slotFeather[4] ?? 2, slotFeather[5] ?? 2, slotFeather[6] ?? 2, slotFeather[7] ?? 2);
		}
		// Set12 followup — per-slot SOFT-UNION opt-in (the smin soft-union is now opt-in, NOT the
		// default).  slot i = 1 iff EVERY region sharing that slot set `softUnion`.  Absent → the
		// neutral all-0 default (no slot is a soft-union participant; equal-dominance pairs use the
		// ordered paint-over — the safe default).
		if (slotSoftUnion && slotSoftUnion.length >= SPLAT_MAX_MATERIALS && mat.uniforms.u_slotSoftUnionA) {
			const sA = mat.uniforms.u_slotSoftUnionA.value as THREE.Vector4;
			const sB = mat.uniforms.u_slotSoftUnionB.value as THREE.Vector4;
			sA.set(slotSoftUnion[0] ?? 0, slotSoftUnion[1] ?? 0, slotSoftUnion[2] ?? 0, slotSoftUnion[3] ?? 0);
			sB.set(slotSoftUnion[4] ?? 0, slotSoftUnion[5] ?? 0, slotSoftUnion[6] ?? 0, slotSoftUnion[7] ?? 0);
		}
		// Set12 P5 — per-slot EDGE-NOISE (the "edge noise gets cut off by the polygon edge"
		// fix): each slot displaces its distance with ITS OWN noise (amp/scale), so a fragment
		// just OUTSIDE the polygon still gets the warp (see SplatTypes.slotNoiseAmp).  Absent →
		// leave the neutral defaults (amp 0 / scale 1 = no displacement — a stale chunk renders
		// a straight edge rather than the old winner-attributed warp).
		if (slotNoiseAmp && slotNoiseAmp.length >= SPLAT_MAX_MATERIALS && mat.uniforms.u_slotNoiseAmpA) {
			const aA = mat.uniforms.u_slotNoiseAmpA.value as THREE.Vector4;
			const aB = mat.uniforms.u_slotNoiseAmpB.value as THREE.Vector4;
			aA.set(slotNoiseAmp[0] ?? 0, slotNoiseAmp[1] ?? 0, slotNoiseAmp[2] ?? 0, slotNoiseAmp[3] ?? 0);
			aB.set(slotNoiseAmp[4] ?? 0, slotNoiseAmp[5] ?? 0, slotNoiseAmp[6] ?? 0, slotNoiseAmp[7] ?? 0);
			const scA = mat.uniforms.u_slotNoiseScaleA.value as THREE.Vector4;
			const scB = mat.uniforms.u_slotNoiseScaleB.value as THREE.Vector4;
			scA.set(slotNoiseScale?.[0] ?? 1, slotNoiseScale?.[1] ?? 1, slotNoiseScale?.[2] ?? 1, slotNoiseScale?.[3] ?? 1);
			scB.set(slotNoiseScale?.[4] ?? 1, slotNoiseScale?.[5] ?? 1, slotNoiseScale?.[6] ?? 1, slotNoiseScale?.[7] ?? 1);
		}
	}

	/** Set the per-chunk region textures (Phase 7): `regionTex` RGBA8UI (the WINNING assignment
	 *  per sample — the region-params ROW index) + `paramsTex` R32F (one row per region; columns =
	 *  the regionModulated params' fixed slots).  Built by {@link buildRegionTextures}; dispose
	 *  both on chunk eviction.  Only set when the program was assembled with `useRegionParams`. */
	setChunkRegionParams(mat: THREE.ShaderMaterial, regionTex: THREE.DataTexture, paramsTex: THREE.DataTexture): void {
		(mat.uniforms.u_splatRegion.value as THREE.Texture) = regionTex;
		(mat.uniforms.u_regionParams.value as THREE.Texture) = paramsTex;
	}

	/** Set a cloned material's u_chunkSize (the chunk's world width in m — maps world XZ → splat
	 *  lattice via u_origin + u_chunkSize; ortho chunks are square, so one float suffices).  Set10:
	 *  also derives the ambient-detail crossover's upper edge (u_ambCellSize = chunkSize/(CHUNK_N-1)
	 *  — the height-lattice spacing) so the shader band follows the chunk's LOD.  The lattice size
	 *  is the shader's `#define CHUNK_N` (33) — the worker's sample lattice (TerrainQuadtree.CHUNK_N);
	 *  a literal here avoids a TerrainQuadtree import (which drags THREE/VectorStructs into the
	 *  Materials module graph, breaking the GPU-lane's import resolution). */
	setChunkSize(mat: THREE.ShaderMaterial, chunkSize: number): void {
		const u = mat.uniforms;
		(u.u_chunkSize.value as number) = chunkSize;
		(u.u_ambCellSize.value as number) = chunkSize / 32;
	}

	/** Set a cloned material's u_origin (the chunk's world-XZ bottom-left). */
	setChunkOrigin(mat: THREE.ShaderMaterial, x: number, y: number): void {
		(mat.uniforms.u_origin.value as THREE.Vector2).set(x, y);
	}

	/** 2026-08-13 — set the terrain-REGION clip on a cloned material (the active TerrainRegion
	 *  world AABBs, each = (minX, minY, maxX, maxY)).  Fragments outside every AABB render at
	 *  alpha 0 (matching the legacy bake path's region clipping); an empty/zero array disables
	 *  the clip (renders fully).  The map's regions are the same for every chunk, so call once
	 *  per frame on each chunk (or whenever the region set changes). */
	setChunkTerrainRegions(mat: THREE.ShaderMaterial, aabbs: readonly {minX: number; minY: number; maxX: number; maxY: number}[]): void {
		const u = mat.uniforms;
		const count = Math.min(aabbs.length, TERRAIN_REGION_CLIP_MAX);
		(u.u_terrainRegionCount.value as number) = count;
		const arr = u.u_terrainRegions.value as THREE.Vector4[];
		for (let i = 0; i < TERRAIN_REGION_CLIP_MAX; i++) {
			const a = aabbs[i];
			if (i < count && a) arr[i].set(a.minX, a.minY, a.maxX, a.maxY);
			else arr[i].set(0, 0, 0, 0);
		}
	}

	/** Pack the recipe's param UBO Float32Array for the given per-instance param overrides
	 *  (keyed by global key `${recipeID}/${paramKey}` — empty for the built-in defaults).
	 *  The result should be written into the paramGroup's uniforms (three re-uploads on change). */
	packParams(overrides: {[globalKey: string]: ParamValue} = {}): Float32Array {
		if (!this.program) throw new Error("[TerrainMaterialManager] ensureProgram must run before packParams");
		return packMaterialParams(this.program.paramLayouts, overrides);
	}

	/** **Param-value refresh (2026-08-08 — the recipe param-edit fix).**  Re-resolve the active
	 *  recipe set and re-pack the SHARED param UBO's Uniform values in place, so a user editing
	 *  a recipe's `paramOverrides` (a params-only change — structurally hash-identical, D1) takes
	 *  effect WITHOUT a program recompile.  The chunk material CLONES share the same UBO reference
	 *  (three.js re-uploads the buffer when a Uniform's value changes — `hasUniformChanged`),
	 *  so updating the group once refreshes every chunk's evaluation on the next render.
	 *
	 *  The set is re-RESOLVED (not just re-packed from cached defaults) because the resolved
	 *  param defaults ARE the override values (`resolveRecipeFromChain` applies `paramOverrides`
	 *  into `ResolvedParam.default`); a re-pack of the stale layouts' cached defaults would be
	 *  a no-op.  The fresh layouts come from a re-assembly (same deterministic used-param order
	 *  for an unchanged structure → index-aligned with the existing group).  The structural-hash
	 *  guard mirrors `ensureProgram`: a set whose STRUCTURE changed (a layer edit) must NOT be
	 *  written into the old program's UBO — the assignment-signature change path in the renderer
	 *  already dropped the program + chunk clones for that case; this refresh is strictly the
	 *  params-only delta.
	 *
	 *  MUTATES each affected program's `paramGroup` in place — the program objects + the chunk
	 *  clones' `uniformsGroups` references stay valid (no re-clone needed).
	 *  @returns true when at least one UBO was re-packed; false when no cached program's set
	 *  matches the current (structural) set — the renderer's assignment-signature path owns a
	 *  structural rebuild. */
	async refreshParamValues(
		recipeIDs: string[],
		opts: {useSplat?: boolean; splatSize?: number; useRegionParams?: boolean; regionParamsColumns?: number; useVariantParams?: boolean; variantRowColumns?: number} = {},
	): Promise<boolean> {
		if (this.programs.size == 0) return false;
		// Re-resolve the CURRENT set (the resolved defaults carry the paramOverrides — see
		// docblock).  Throws propagate to the caller (the renderer logs + keeps the old values).
		const {ResolveRecipe} = await import("../../Server/DB/mapObjects/ProcMatAccessors.js");
		const editedIDs = new Set(recipeIDs);
		// Cache each recipe's fresh resolve on demand.  The memo MUST cover a program's WHOLE set,
		// which can include ids BEYOND the caller's `recipeIDs` (a per-chunk program may hold a
		// low-dominance region the capped `splatRecipeIDs` dropped) — so resolve lazily per-program.
		const resolvedByID = new Map<string, ResolvedRecipe>();
		const ensureResolved = (id: string): ResolvedRecipe => {
			let r = resolvedByID.get(id);
			if (r == null) { r = ResolveRecipe(id, this.mapID); resolvedByID.set(id, r); }
			return r;
		};
		const useSplat = opts.useSplat === true;
		const useRegionParams = opts.useRegionParams === true && useSplat;
		const useVariantParams = opts.useVariantParams !== false && useSplat;
		const flagKey = (useSplat ? ":lit:splat" : ":lit") + (useRegionParams ? ":regionParams" : "") + (useVariantParams ? ":variantParams" : "");

		// 2026-08-13 — refresh EVERY cached program whose set includes an edited recipe, not just
		// the fast-path `this.program`.  Chunks render from PER-CHUNK programs (getProgramForSet):
		// a fallback-only chunk uses the base-only prefix program, and with >SPLAT_MAX_MATERIALS
		// distinct recipes each chunk gets a different 4-way subset (the rasterizer caps per chunk
		// by dominance).  Those programs have their OWN paramGroup, which the old code never
		// touched — so editing the fallback's params didn't live-update those chunks.
		//
		// For each program whose set overlaps the edited set AND still matches its setHash
		// structurally (params-only edit → hashes preserved), re-resolve that program's own set,
		// re-assemble for FRESH layouts (deterministic used-param order → index-aligned with the
		// existing group), and write the packed floats into THAT program's group in place.  A
		// structurally-stale program (setHash mismatch) is owned by the assignment-signature path.
		let refreshedAny = false;
		for (const prog of this.programs.values()) {
			if (!prog.recipeIDs.some(id=>editedIDs.has(id))) continue; // this set is unaffected
			const progResolved = prog.recipeIDs.map(id=>ensureResolved(id));
			const progSetHash = progResolved.map(r=>r.structuralHash).join("~") + flagKey;
			if (progSetHash !== prog.setHash) continue; // structurally stale — assignment path owns it
			const assembled = assembleMaterialShader(progResolved, {
				includeLighting: true, useSplat, splatSize: opts.splatSize,
				useRegionParams, regionParamsColumns: opts.regionParamsColumns,
				useVariantParams, variantRowColumns: opts.variantRowColumns,
				roughnessAwareSpecular: true,
				terrainRegionClip: true,
				outputTransfer: true,
			});
			const packed = packMaterialParams(assembled.paramLayouts, {});
			writePackedParamsIntoGroup(prog.paramGroup, prog.paramLayouts, packed);
			refreshedAny = true;
		}
		// Set6→Set7 — also refresh every Layer-2 VARIANT whose identity set includes an edited
		// recipe (the chunks render from these per-identity ROWS, NOT the program's default
		// group).  A variant's ROW is re-packed from ITS OWN identity set's fresh resolve (the
		// layout is rebuilt by re-collecting used params from the fresh resolve — deterministic
		// order → same columns), and the row's floats are rewritten INTO the shared variant
		// texture in place (`needsUpdate` → cheap next-render pick-up, no program recompile).
		// Skip variants whose identity set is structurally stale (owned by the assignment path).
		if (useVariantParams) {
			for (const variant of this.variants.values()) {
				if (!variant.recipeIDs.some(id=>editedIDs.has(id))) continue;
				const variantResolved = variant.recipeIDs.map(id=>ensureResolved(id));
				const progSetHash = variantResolved.map(r=>r.structuralHash).join("~") + flagKey;
				if (progSetHash !== variant.program.setHash) continue; // structurally stale
				// Re-pack THIS variant's own row with the fresh resolve (each layout's
				// defaultValue carries the fresh resolve — packVariantParamsRow with {} uses it).
				const usedParams = collectUsedParams(variantResolved);
				const layouts = buildVariantParamLayouts(usedParams, this.variantRowFloatWidth);
				const values = packVariantParamsRow(layouts, {}, this.variantRowFloatWidth);
				// Rewrite in place into the shared texture + update the variant's stored values.
				// Row stride = variantRowFloatWidth floats (NOT *4 — see packVariantRow).
				if (this.variantData) this.variantData.set(values, variant.row * this.variantRowFloatWidth);
				if (this.variantTexture) this.variantTexture.needsUpdate = true;
				variant.values = values;
				variant.paramLayouts = layouts;
				refreshedAny = true;
			}
		}
		return refreshedAny;
	}

	/** Guard 2 (§9.5) — release ALL variant rows (reset every refcount + reclaim every row).
	 *  Called when the renderer bulk-releases every chunk (region-signature change, data-key
	 *  change, unmount) — the manager persists but no chunk holds a variant anymore, so every
	 *  row is free for reuse.  Cheap; the next frame re-acquires + re-packs as chunks appear. */
	releaseAllVariants(): void {
		for (const key of this.variantRowRefs.keys()) {
			const variant = this.variants.get(key);
			if (variant) { this.variantRowFreeList.push(variant.row); this.variants.delete(key); }
		}
		this.variantRowRefs.clear();
	}

	/** TEMP DEBUG (2026-08-26) — black-chunk probe: readback the LIVE variant texture's GPU
	 *  contents row-by-row through a small FBO (gl.readPixels on a framebuffer attachment) and
	 *  compare against the CPU-side `variantData`.  A row that is correct CPU-side but zeros
	 *  GPU-side = a stale/failed upload.  Devtools-console only; one-off. */
	__testOnly_readbackVariantRows(rows?: number[]): Record<string, unknown> {
		const gl = this.gl;
		const tex = this.variantTexture;
		if (!gl || !tex) return {error: "no gl/textures"};
		const props = this.threeRenderer?.properties.get(tex) as {__webglTexture?: WebGLTexture} | undefined;
		const glTex = props?.__webglTexture;
		if (!glTex) return {error: "texture never uploaded to GL"};
		const w = tex.image.width as number;
		const h = tex.image.height as number;
		const out: Record<string, unknown> = {cpuWidthTexels: w, cpuHeight: h, rows: {} as Record<number, unknown>};
		const fb = gl.createFramebuffer();
		gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, glTex, 0);
		const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
		out.fbStatus = status === gl.FRAMEBUFFER_COMPLETE ? "complete" : `incomplete(0x${status.toString(16)})`;
		if (status === gl.FRAMEBUFFER_COMPLETE) {
			const targets = rows ?? Array.from({length: h}, (_u, i)=>i);
			const buf = new Float32Array(w * 4);
			for (const row of targets) {
				gl.readPixels(0, row, w, 1, gl.RGBA, gl.FLOAT, buf);
				const head = Array.from(buf.slice(0, 8)).map(v=>Math.round(v * 1000) / 1000);
				(out.rows as Record<number, unknown>)[row] = {head};
			}
		}
		gl.bindFramebuffer(gl.FRAMEBUFFER, null);
		gl.deleteFramebuffer(fb);
		return out;
	}

	/** Dispose the compiled program(s) + cache (called on renderer unmount). */
	dispose(): void {
		__testOnly_clearProgramCache();
		// Set13 — NEVER dispose a still-compiling program synchronously: three's material
		// dispose → releaseProgram → deleteProgram on an un-linked program forces the driver to
		// finish that compile SYNCHRONOUSLY — the exact teardown-time lockup (leaving the map-ui
		// while a large shader compiles ⇒ multi-second freeze).  The settled ones go now; the
		// in-flight ones are disposed once their compiles complete (awaited asynchronously, so
		// the main thread stays responsive — the unmount has already stopped rendering them).
		// ALSO: guard r3f's canvas-unmount `gl.forceContextLoss()` the same way (see
		// guardContextLossWhileCompiling) — destroying the WebGL CONTEXT mid-compile forces the
		// same driver synchronization, and r3f calls it regardless of our material disposal.
		const guardedRenderer = this.guardContextLossWhileCompiling();
		void this.disposeProgramsSafely().finally(()=>{
			this.unguardContextLoss(guardedRenderer);
		});
		this.variants.clear();
		this.resolvedSetHashByIds.clear();
		if (this.variantTexture) this.variantTexture.dispose();
		this.variantTexture = null;
		this.variantData = null;
		this.nextVariantRow = 1;
		this.program = null;
		this.setHash = "";
		// Set13 — reset the compile counters (any in-flight compileAsync resolves harmlessly;
		// its noteCompileResolved just decrements from 0 → clamps to 0).  Lazy import (see
		// noteCompileIssued — keeps TerrainProcessStats.ts out of the browser GPU test lane).
		this.materialCompilesPending = 0;
		this.materialCompilesTotal = 0;
		void import("../Heights/TerrainProcessStats.js").then(({terrainProcessStats})=>{
			RunInAction_Set(()=>{
				terrainProcessStats.materialCompilesPending = null;
				terrainProcessStats.materialCompilesTotal = 0;
			});
		});
		this.threeRenderer = null;
	}

	/** Set13 — stub the renderer's `forceContextLoss` while ANY program compile is still in
	 *  flight.  r3f's canvas-unmount teardown calls `state.gl.forceContextLoss()`
	 *  (unmountComponentAtNode → setTimeout → ... → loseContext), and WEBGL_lose_context's spec
	 *  says implementations "should destroy the underlying graphics context and all graphics
	 *  resources" — destroying the context while the driver's shader-compiler threads are
	 *  mid-compile forces a synchronous main-thread block (the leave-the-map lockup, verified
	 *  2026-08-31).  Stubbing the method makes r3f's optional-call (`gl.forceContextLoss?.()`)
	 *  hit OUR no-op instead; the context is then simply GC'd with the canvas once the compiles
	 *  settle (no explicit destruction needed).  Idempotent; only stubs when a compile is in
	 *  flight ({@link materialCompilesPending} > 0). */
	private guardContextLossWhileCompiling(): THREE.WebGLRenderer | null {
		const renderer = this.threeRenderer;
		if (renderer == null) return null;
		if (this.materialCompilesPending <= 0) return null; // nothing in flight — nothing to guard
		const rendererAny = renderer as unknown as {forceContextLoss?: ()=>void; __set13OriginalForceContextLoss?: ()=>void; __set13ForceContextLossGuarded?: boolean};
		if (rendererAny.__set13ForceContextLossGuarded) return renderer; // already stubbed
		rendererAny.__set13OriginalForceContextLoss = rendererAny.forceContextLoss?.bind(renderer);
		rendererAny.forceContextLoss = ()=>{ /* Set13 guard: context destruction deferred until compiles settle */ };
		rendererAny.__set13ForceContextLossGuarded = true;
		return renderer;
	}

	/** Set13 — restore the original `forceContextLoss` (called once the deferred program
	 *  disposal settles — after every in-flight compile completed).  Takes the renderer INSTANCE
	 *  (captured at guard time) because `this.threeRenderer` may already be nulled.  If the
	 *  canvas is already unmounted by then, the restored method simply operates on a dead
	 *  renderer (harmless). */
	private unguardContextLoss(renderer: THREE.WebGLRenderer | null): void {
		if (renderer == null) return;
		const rendererAny = renderer as unknown as {forceContextLoss?: ()=>void; __set13OriginalForceContextLoss?: ()=>void; __set13ForceContextLossGuarded?: boolean};
		if (!rendererAny.__set13ForceContextLossGuarded) return;
		if (rendererAny.__set13OriginalForceContextLoss) rendererAny.forceContextLoss = rendererAny.__set13OriginalForceContextLoss;
		else delete rendererAny.forceContextLoss;
		rendererAny.__set13ForceContextLossGuarded = false;
	}
}

/** **Write packed param floats into a UBO's Uniform values in place (2026-08-08).**  The shared
 *  param group's Uniforms are laid out in the SAME order as the packed Float32Array, each at its
 *  layout's `offsetFloats` (std140 — `packMaterialParams` writes there, so this reads there).
 *  three.js's UBO uploader (`WebGLUniformsGroups.updateBufferData`) diffs each Uniform's value
 *  against its per-frame cache and bufferSubData's only what changed — so mutating the Uniforms
 *  in place makes the next render pick the new values up with zero shader work.  Ramps are stored
 *  as a NESTED ARRAY of Uniforms (one per stop — see `buildParamGroup`'s nested-array note).
 *  Exported so the GPU test lane can drive the SAME code path `refreshParamValues` uses (a
 *  param-change render test must not re-implement the write logic). */
export function writePackedParamsIntoGroup(paramGroup: THREE.UniformsGroup, layouts: PackedParamLayout[], packed: Float32Array): void {
	for (let i = 0; i < layouts.length; i++) {
		const layout = layouts[i];
		const uniform = paramGroup.uniforms[i];
		const base = layout.offsetFloats;
		if (layout.kind == "scalar") {			((uniform as THREE.Uniform).value as number) = packed[base];
		} else if (layout.kind == "color") {
			((uniform as THREE.Uniform).value as THREE.Vector4).set(packed[base], packed[base + 1], packed[base + 2], packed[base + 3]);
		} else {
			// ramp — one Uniform PER stop; the group entry is a NESTED ARRAY of Uniforms
			// (see buildParamGroup's nested-array note — `paramGroup.add([u0, u1, ...])`).
			const stopUniforms = uniform as THREE.Uniform[];
			for (let s = 0; s < stopUniforms.length; s++) {
				(stopUniforms[s].value as THREE.Vector4).set(
					packed[base + s * 4], packed[base + s * 4 + 1],
					packed[base + s * 4 + 2], packed[base + s * 4 + 3],
				);
			}
		}
	}
}

/** The `#version` header the assembler prepends (self-contained raw fragment).  three.js ALSO
 *  prepends `#version 300 es` + its prefix for non-raw ShaderMaterials — handing it the full
 *  assembled source produces a DOUBLE `#version` (a GLSL-ES-300 compile error: "the #version
 *  directive must occur before anything else").  The ShaderMaterial fragment must be the
 *  version-STRIPPED body; the raw `compileRawFragmentProgram` path keeps the full source. */
const ASSEMBLED_VERSION_LINE = "#version 300 es\n";

/** Strip the leading `#version 300 es` line from an assembled fragment (for the three.js
 *  ShaderMaterial, which prepends its own).  Assembles always emit it as the first line. */
function stripVersionHeader(glsl: string): string {
	return glsl.startsWith(ASSEMBLED_VERSION_LINE) ? glsl.slice(ASSEMBLED_VERSION_LINE.length) : glsl;
}

/** Build the three.js ShaderMaterial template for an assembled fragment: glslVersion GLSL3 (three
 *  prepends `#version 300 es` + precision + prefix for non-raw ShaderMaterials — hence the
 *  version-stripped fragment), three's standard vertex, the full uniform decl set, and the shared
 *  param UBO.  Shared by the live template (`ensureProgram`) and the Phase 6 three.js-layer
 *  warm-up scratch materials — IDENTICAL construction so their three.js program cacheKeys match
 *  (customFragmentShaderID = per-fragmentShader-STRING id from WebGLShaderCache) + uniform
 *  booleans + customProgramCacheKey (onBeforeCompile.toString() — same for all our
 *  materials) + the scene's light counts (ambient lights don't count).  A scratch
 *  ShaderMaterial built identically to the live template (same fragment, same uniform
 *  decls) therefore lands on the SAME cacheKey.  The scratch must be KEPT ALIVE until the
 *  live template appears — three destroys a program when its last referencing material is
 *  GC'd (usedTimes → 0), so the warm-up holds the scratches in `warmMaterials`.
 *  Exported so the GPU lane can construct byte-identical scratch/live materials. */
export function buildMaterialTemplate(
	fragment: string,
	useSplat: boolean,
	_paramLayouts: PackedParamLayout[],
	THREE: typeof import("three"),
	paramGroup: () => THREE.UniformsGroup,
	useRegionParams = false,
	useVariantParams = false,
): THREE.ShaderMaterial {
	const material = new THREE.ShaderMaterial({
		glslVersion: THREE.GLSL3,
		vertexShader: MATERIAL_VERTEX_GLSL,
		// The version-stripped body (three prepends its own `#version 300 es` + prefix).
		fragmentShader: stripVersionHeader(fragment),
		uniforms: {
			u_origin: {value: new THREE.Vector2(0, 0)},
			u_metersPerPixel: {value: 1},
			u_baseAlbedo: {value: new THREE.Vector3(1, 1, 1)},
			// 2026-08-13 — terrain-REGION clip (start neutral: count 0 = no clip — renders fully).
			// setChunkTerrainRegions populates them per chunk (the active TerrainRegion AABBs).
			u_terrainRegionCount: {value: 0},
			u_terrainRegions: {value: Array.from({length: TERRAIN_REGION_CLIP_MAX}, ()=>new THREE.Vector4(0, 0, 0, 0))},
			// Phase 5 splat: per-chunk u_chunkSize + the splat DataTextures (start neutral —
			// setChunkSize/setChunkSplat populate them per chunk).
			u_chunkSize: {value: 1},
			...(useSplat ? {
				u_splatIndex: {value: null},
				u_splatDist: {value: null},
				// Set7 §9 — the second distance texture (slots 4–7 of the ≤8-way splat; RGBA32F,
				// LINEAR — same as u_splatDist).  The assembler emits u_splatDist2 only when the
				// set actually uses >4 recipes, so a ≤4-way material has no such uniform (this
				// template entry is harmless — three ignores unused uniforms).
				u_splatDist2: {value: null},
				// P5 — per-slot EFFECTIVE DOMINANCE (the smin soft-union's gate, plan §4.2 C).
				// Start DISTINCT (A=0/1/2/3, B=4/5/6/7 — slot i ≠ slot j) so a chunk whose
				// slotDominance was never set renders the P3 ordered paint-over (the pair gate's
				// not-equal branch is byte-identical to P3).  setChunkSplatDist overwrites them
				// from the chunk.  Set7 §9 — two vec4s (slots 0–3 + slots 4–7).
				u_slotDominanceA: {value: new THREE.Vector4(0, 1, 2, 3)},
				u_slotDominanceB: {value: new THREE.Vector4(4, 5, 6, 7)},
				// Set12 followup — per-slot RESOLVED FEATHER (the "fade-to-wrong-material" fix).
				// Start neutral at the class default feather (2) so a chunk whose slotFeather was
				// never set keeps the old single-g_regionFeather-ish behaviour; setChunkSplatDist
				// overwrites them from the chunk.  Set7 §9 — two vec4s (slots 0–3 + slots 4–7).
				u_slotFeatherA: {value: new THREE.Vector4(2, 2, 2, 2)},
				u_slotFeatherB: {value: new THREE.Vector4(2, 2, 2, 2)},
				// Set12 followup — per-slot SOFT-UNION opt-in (the smin soft-union is now opt-in,
				// NOT the default).  Start all-0 (no slot is a soft-union participant) so a chunk
				// whose slotSoftUnion was never set uses the ordered paint-over — the safe default
				// (equal-dominance pairs with a far-but-bbox-overlapping member can't phantom-
				// blend).  setChunkSplatDist overwrites them from the chunk.  Set7 §9 — two vec4s.
				u_slotSoftUnionA: {value: new THREE.Vector4(0, 0, 0, 0)},
				u_slotSoftUnionB: {value: new THREE.Vector4(0, 0, 0, 0)},
				// Set12 P5 — per-slot EDGE-NOISE amplitude/scale (the "edge noise gets cut off
				// by the polygon edge" fix).  Start neutral (amp 0 / scale 1 = no displacement)
				// so a chunk whose slotNoiseAmp was never set renders a straight edge;
				// setChunkSplatDist overwrites them from the chunk.  Set7 §9 — two vec4s each.
				u_slotNoiseAmpA: {value: new THREE.Vector4(0, 0, 0, 0)},
				u_slotNoiseAmpB: {value: new THREE.Vector4(0, 0, 0, 0)},
				u_slotNoiseScaleA: {value: new THREE.Vector4(1, 1, 1, 1)},
				u_slotNoiseScaleB: {value: new THREE.Vector4(1, 1, 1, 1)},
			} : {}),
			// Phase 7 region params: the per-chunk region texture + the per-map params texture
			// (start neutral — setChunkRegionParams populates them per chunk).
			...(useSplat && useRegionParams ? {
				u_splatRegion: {value: null},
				u_regionParams: {value: null},
			} : {}),
			// Set7 — the per-identity VARIANT params texture + row (start neutral: row 0, texture
			// null — setChunkVariantRow binds the shared texture + row per chunk).  A null texture
			// with u_variantRow 0 reads the fallback row once the template material's program uses
			// the variant path; the renderer MUST call setChunkVariantRow before first render.
			...(useSplat && useVariantParams ? {
				u_variantParams: {value: null},
				u_variantRow: {value: 0},
			} : {}),
			// 2026-08-25 — DEBUG SHIM per-slot colours (vec4 array, rgb = pre-pow colour, a =
			// per-material seed).  Start neutral (zeros — the shader falls back to a slot-index
			// decoration); ensureProgram populates it with the hashed colours when the shim is
			// active.  three skips the uniform when the GLSL doesn't reference it (non-shim path).
			u_debugShimColors: {value: new Array(DEBUG_SHIM_MAX_MATERIALS).fill(null).map(()=>new THREE.Vector4(0, 0, 0, 0))},
			u_lightDir: {value: new THREE.Vector3(0.7071, 0, 0.7071)},
			u_ambient: {value: 0.2},
			u_sunIntensity: {value: 1.0},
			u_slopeScale: {value: 4.0},
			// Set15 P3 (D10) — the camera's world position, for the REAL view vector in specular.
			// Set each frame from `camera.position` (not three's built-in `cameraPosition`, which
			// the raw compile path lacks).  Start neutral — the gating `u_displaceScale` is 0 in
			// 2D, so the hard-coded (0,0,1) view is used there regardless.
			u_cameraPos: {value: new THREE.Vector3(0, 0, 1500)},
			// Set15 P8 (§6.2) — shadow uniforms. Start neutral (u_shadowEnabled 0 → getShadowMask
			// returns 1.0 without sampling). The renderer sets these per chunk via setShadowUniforms.
			u_shadowMap: {value: null},
			u_shadowMatrix: {value: new THREE.Matrix4()},
			u_shadowMapSize: {value: new THREE.Vector2(2048, 2048)},
			// NEGATIVE — see getShadowMask: `coord.z += u_shadowBias` then `step(coord.z, depth)`, so
			// a POSITIVE bias causes MORE self-shadowing (acne), not less. Only a placeholder (the
			// renderer overwrites it from the light), but keep the sign honest so it can't mislead.
			u_shadowBias: {value: -0.0015},
			u_shadowEnabled: {value: 0},
			// Set10 — ambient-detail (residual band) uniforms: per-config values set with the
			// light rig (setFrameUniforms) + the per-chunk cell size folded into setChunkSize.
			// Start neutral: detailStrength 1 (the §4.3 physically-consistent default), octaves 0
			// (no band until the renderer sets the config), cellSize 1.
			u_ambCellSize: {value: 1},
			u_ambWavelength: {value: 1000},
			u_ambLacunarity: {value: 2.0},
			u_ambPersistence: {value: 0.5},
			u_ambAmplitude: {value: 0},
			u_ambDetailStrength: {value: 1},
			u_ambOctaves: {value: 0},
			u_ambDetailOctaves: {value: 0},
			u_ambFractalType: {value: 0},
			u_ambOffset: {value: new THREE.Vector2(0, 0)},
			u_ambSeed: {value: 0},
			// Per-chunk per-sample geometric normal lattice (the worker's normals — slope_scale
			// ALREADY applied worker-side).  Start neutral (null — setChunkGeoNormal populates it
			// per chunk; texelFetch of an unbound texture is undefined, so the renderer MUST set
			// it before the first render of each chunk).
			u_geoNormal: {value: null},
			// 2026-08-13 — per-chunk HEIGHT lattice (u_geoHeight, the worker's CHUNK_N×CHUNK_N
			// heights, R32F — the `geoHeight` primitive's elevation-reactive colour input).  Start
			// neutral (null — setChunkGeoHeight populates it per chunk; texelFetch of an unbound
			// texture is undefined, so the renderer MUST set it before the first render of each
			// chunk that uses the geoHeight primitive).
			u_geoHeight: {value: null},
			// Set15 P3 (§3.4) — the D-LESS height lattice (R32F, same shape as u_geoHeight), the
			// VERTEX-displacement input.  Start neutral (null — setChunkGeoHeightGeom populates it
			// per chunk; the renderer binds it for every chunk even in 2D so the `u_displaceScale`-
			// gated vertex fetch always reads valid data).  The field excludes D (the fragment
			// renders D at pixel scale), so displacing by it can't double-count the ambient detail.
			u_geoHeightGeom: {value: null},
			// Set15 P3 (D4) — geometry displacement gate: 0 in 2D (flat quads — byte-identical),
			// 1 in 3D (displace by u_geoHeightGeom).  Also gates the 3D-only matPixelSize fwidth
			// (D9), the region-clip discard (D5) and the real view vector for specular (D10).
			u_displaceScale: {value: 0},
			// Set15 P6 (§3.7) — per-edge COARSENESS LEVELS for the generalized edge stitch, in
			// [south, east, north, west] order (k = the edge's neighbour is k levels coarser; 0 =
			// same/finer → no stitch on that edge).  Start neutral (no coarser edges → no stitch).
			// Set per 3D chunk via setChunkEdgeCoarser; ignored when `u_edgeStitchEnabled` is 0 (2D).
			u_edgeCoarserLevel: {value: new THREE.Vector4(0, 0, 0, 0)},
			// Set15 P6 (D7) — 1 when the edge stitch is active (3D).  Enabling it DISABLES the skirt
			// drop: the stitch makes the LOD gap exactly zero, so the perimeter apron is no longer needed.
			// 2D (or stitch disabled) keeps the pre-P6 skirt behavior byte-identical.
			u_edgeStitchEnabled: {value: 0},
			// Issue 2 (2026-08-08) — per-chunk ambient-weight mask (R32F) + enable flag.  Start
			// disabled (no mask → the shader's ambientDetailNormal skips the fetch, byte-identical
			// to pre-issue-2); setChunkAmbientWeight populates it per chunk with a mask.
			u_ambientWeightMask: {value: null},
			u_ambientWeightMaskEnabled: {value: 0},
			// Phase 4 — normal-only specular lobe (set-wide constant; per-frame uniform for
			// Phase 7 slider wiring, but the default is applied here so nothing else changes).
			u_specStrength: {value: DEFAULT_SPECULAR_STRENGTH},
			// Set11 Phase 2 Toksvig widening gain, required by the roughnessAwareSpecular path (the
			// shader widens rough by the sub-pixel slope variance).  0 = exact no-op if it misbehaves.
			u_varianceGain: {value: DEFAULT_VARIANCE_GAIN},
			u_opacity: {value: 0.5},
		},
		transparent: true,
		depthWrite: false,
		blending: THREE.NormalBlending,
	});
	// Set7/2026-08-25 — when the variant-texture path is active, ALL scalar/color/ramp params
	// read via texelFetch (the assembler emits NO MaterialParams block — see ShaderAssembler
	// emitUBO), so the material has NO UniformsGroup → no UBO binding point per program.  Only
	// the non-variant fallback path (useVariantParams:false) attaches the param UBO.
	if (!useVariantParams) material.uniformsGroups = [paramGroup()];
	return material;
}

/** The standard three.js GLSL3 vertex shader for a ShaderMaterial.  three prepends its own
 *  `#version 300 es` + `precision` prefix for ShaderMaterial (non-raw), so this must NOT carry
 *  a version directive — three injects it (WebGLProgram.js: for ShaderMaterial,
 *  `versionString = '#version 300 es'` is added and `#define attribute in` etc. applied).
 *  `position` is three's standard vertex attribute; the standard matrices are provided.
 *
 *  Chunk-local UV: `uv` is three's standard PlaneGeometry attribute (range [0,1] across the
 *  chunk).  Passed to the fragment as `vUvLocal` so the fragment can compute the world-XZ
 *  position as `u_origin + vUvLocal * u_chunkSize` — the chunk's bottom-left + its world size.
 *  (gl_FragCoord is CANVAS-space, not chunk-space; using it as the position source restarts
 *  every procedural field at each chunk boundary, producing seams.)
 *
 *  Set15 P3 (§3.3/§3.7) — displacement + skirt, gated by `u_displaceScale` (D4):
 *   - The chunk geometry is the shared 33×33 subdivided grid (ChunkMeshPool's `geo3D`) in unit
 *     space [0,1]², scaled to world metres by `mesh.scale = (bounds.width, bounds.height, 1)`.
 *     Local z is already world metres, so `transformed.z += h` needs no scaling.
 *   - Each vertex's height comes from the D-less `u_geoHeightGeom` lattice (D6).  The lattice
 *     coord `round(uv * (CHUNK_N - 1))` lands EXACTLY on the worker's sample, byte-identical to
 *     the fragment's `matGeoHeight()` mapping — so the surface is seam-free at same-LOD chunk
 *     boundaries (both chunks sample the same texel at the shared edge).
 *   - The skirt: perimeter vertices are DUPLICATED in the geometry flagged `aSkirt = 1` and
 *     pushed DOWN by a chunk-proportional amount, backing the LOD crack at a coarse/fine
 *     boundary (§3.7).  In 2D (`u_displaceScale` 0) both displacement and skirt are no-ops and
 *     the (2-triangle PlaneGeometry) quads stay byte-identical. */
export const MATERIAL_VERTEX_GLSL = `
// The worker's height lattice (TerrainQuadtree.CHUNK_N).  The VERTEX displacement samples the
// D-less u_geoHeightGeom lattice (always 33×33); the FRAGMENT's CHUNK_N (splat lattice) is a
// separate define in the other shader stage.  Matches the geometry's 33×33 subdivision count.
#define CHUNK_N 33
// Set15 P3 (§3.7) — skirt depth as a fraction of the chunk's world width.  Proportional to the
// chunk so coarse far chunks get a proportionally deep apron (the LOD crack at a boundary scales
// with the coarser neighbour's cell size).  A constant hidden below the surface; tuned modestly
// so the apron doesn't visibly poke through steep terrain below.  Set15 P6 — the skirt is
// REPLACED by the exact edge stitch (D7), so it only applies when stitching is DISABLED (see
// u_edgeStitchEnabled below; the gate keeps the pre-P6 behavior byte-identical when off).
#define SKIRT_DEPTH_FRACTION 0.05
uniform float u_displaceScale;    // 0 in 2D (flat quads), 1 in 3D (displaced) — D4
uniform float u_chunkSize;        // the chunk's world width (m) — skirt depth scales with it
uniform sampler2D u_geoHeightGeom; // D-less height lattice (R32F, CHUNK_N×CHUNK_N) — the §3.4 displacement field
attribute float aSkirt;           // 1 on the skirt ring (pushed down), 0 on the surface grid
// Set15 P6 (§3.7) — per-edge COARSENESS LEVELS, in [south, east, north, west] order: k = the
// edge's neighbour is k levels coarser; 0 = same/finer (no stitch on that edge).  A border
// vertex at lattice index i on a flagged edge is stitched by lerping between the two nearest
// coarse samples — which are this chunk's OWN lattice samples at indices i0 = (i / 2^k)*2^k and
// i0 + 2^k (the quadtree is aligned, so a 2^k:1-coarser neighbour's samples land on OUR
// 2^k-spaced indices).  This handles ANY power-of-two level ratio, so no 2:1-balance pass is
// needed.  Only border vertices NOT on a shared coarse sample are stitched (see below).
uniform vec4 u_edgeCoarserLevel;
// Set15 P6 (D7) — 1 when the edge stitch is active (3D).  Enabling it DISABLES the skirt
// drop: the stitch makes the LOD gap exactly zero, so the perimeter apron is no longer needed.
// 2D (or stitch disabled) keeps the pre-P6 skirt behavior byte-identical.
uniform float u_edgeStitchEnabled;
out vec2 vUvLocal;
out vec3 vWorldPos;
void main() {
	vUvLocal = uv;
	#include <begin_vertex>     // vec3 transformed = vec3(position);
	// P3 — displace by the D-less height lattice (u_displaceScale-gated; 0 in 2D → no-op, so
	// the ortho path is byte-identical — D4).  Same round() mapping as the fragment's
	// matGeoHeight, so the surface is seam-free at same-LOD chunk boundaries.
	ivec2 lat = ivec2(round(uv * float(CHUNK_N - 1)));
	float h = texelFetch(u_geoHeightGeom, lat, 0).r;
	// P6 (§3.7) — GENERALIZED edge stitch (replaces skirts, D7).  A border vertex on an edge
	// whose neighbour is coarser by k levels is re-sampled to the coarse edge's linear
	// interpolation.  Because the quadtree is ALIGNED, a 2^k:1-coarser neighbour's samples land
	// on THIS chunk's OWN lattice indices that are multiples of 2^k — so the two nearest coarse
	// samples are i0 = (i / 2^k)*2^k and i0 + 2^k (both present in u_geoHeightGeom), and the
	// coarse edge's value at our index i is the lerp h[i0]*(1-t) + h[i0+2^k]*t with
	// t = (i - i0) / 2^k.  This is EXACT for any power-of-two level ratio (no 2:1-balance
	// requirement, no neighbour data).  A vertex that IS on a coarse sample (i % 2^k == 0) is
	// shared with the coarse neighbour (never stitched); corners (index 0/CHUNK_N-1) are always
	// multiples of any 2^k, so never stitched.  A border vertex is on exactly one edge, so at
	// most one axis's delta applies.
	{
		int i = lat.x, j = lat.y;
		float south = (j == 0)          ? u_edgeCoarserLevel.x : 0.0;
		float east  = (i == CHUNK_N-1)  ? u_edgeCoarserLevel.y : 0.0;
		float north = (j == CHUNK_N-1)  ? u_edgeCoarserLevel.z : 0.0;
		float west  = (i == 0)          ? u_edgeCoarserLevel.w : 0.0;
		if (u_edgeStitchEnabled > 0.5 && (south + east + north + west) > 0.5) {
			// Pick the flagged edge (a border vertex is on exactly one edge; if more than one is
			// flagged — a corner — it's a shared coarse sample and step-modulo below is 0, so the
			// fall-through leaves h untouched).
			if (south > 0.5) {
				int step = 1 << int(south); // 2^k
				if (i % step != 0) {
					int i0 = (i / step) * step;
					float t = float(i - i0) / float(step);
					h = mix(texelFetch(u_geoHeightGeom, ivec2(i0, 0), 0).r, texelFetch(u_geoHeightGeom, ivec2(i0 + step, 0), 0).r, t);
				}
			} else if (north > 0.5) {
				int step = 1 << int(north);
				if (i % step != 0) {
					int i0 = (i / step) * step;
					float t = float(i - i0) / float(step);
					h = mix(texelFetch(u_geoHeightGeom, ivec2(i0, CHUNK_N-1), 0).r, texelFetch(u_geoHeightGeom, ivec2(i0 + step, CHUNK_N-1), 0).r, t);
				}
			} else if (west > 0.5) {
				int step = 1 << int(west);
				if (j % step != 0) {
					int j0 = (j / step) * step;
					float t = float(j - j0) / float(step);
					h = mix(texelFetch(u_geoHeightGeom, ivec2(0, j0), 0).r, texelFetch(u_geoHeightGeom, ivec2(0, j0 + step), 0).r, t);
				}
			} else if (east > 0.5) {
				int step = 1 << int(east);
				if (j % step != 0) {
					int j0 = (j / step) * step;
					float t = float(j - j0) / float(step);
					h = mix(texelFetch(u_geoHeightGeom, ivec2(CHUNK_N-1, j0), 0).r, texelFetch(u_geoHeightGeom, ivec2(CHUNK_N-1, j0 + step), 0).r, t);
				}
			}
		}
	}
	transformed.z += h * u_displaceScale;
	// P3 — skirt (§3.7): perimeter verts (aSkirt = 1) drop below the surface by a
	// chunk-proportional amount, so the vertical gap at a coarse/fine LOD boundary is backed
	// by geometry instead of showing background through.  In 2D the skirt flag is never set
	// (the 2-triangle PlaneGeometry has no aSkirt) and the gate is 0 — no-op.
	// Set15 P6 (D7) — the skirt is REPLACED by the exact edge stitch: when u_edgeStitchEnabled
	// is 1, the apron is not dropped (the stitch makes the gap exactly zero).  When off, the
	// pre-P6 skirt behavior is preserved byte-identically.
	transformed.z -= (u_chunkSize * SKIRT_DEPTH_FRACTION) * u_displaceScale * aSkirt * (1.0 - u_edgeStitchEnabled);
	// D10 — the world position of the displaced vertex, for a real view vector in specular.
	// (modelMatrix moves the unit-space vertex to its chunk world position; local z is metres.)
	vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
	#include <project_vertex>
}`;

/** Compile + link a fragment-shader-only WebGL2 program (the material fragment carries its own
 *  `#version 300 es` + `precision` — the assembler emits them).  Mirrors the Poisson WebGL port's
 *  `compileLinkProgram` shape for symmetry + error surfacing.  three's ShaderMaterial compiles
 *  the same source through its own WebGLProgram on first render (deduped by source), so the
 *  D1 program-cache semantics hold at both layers. */
export function compileRawFragmentProgram(gl: WebGL2RenderingContext, fragmentSrc: string): WebGLProgram {
	const vs = gl.createShader(gl.VERTEX_SHADER);
	if (!vs) throw new Error("[TerrainMaterialManager] createShader(vertex) failed");
	// Fullscreen triangle (3 vertices, positions derived from gl_VertexID — same as the Poisson
	// port's compileLinkProgram; WebGL cannot LINK a program with a fragment shader only).
	// Emits `vUvLocal` to match the assembled fragment's `in vec2 vUvLocal;` (the chunk-local
	// UV; the raw path is a warm-up/compile-check only — the test renders nothing through this
	// program, so any non-NaN UV is fine; the live render path goes through three.js' own vertex
	// shader, which writes the real `uv` attribute from PlaneGeometry).  Set15 P3 (D10): the
	// fragment ALSO declares `in vec3 vWorldPos;` (used by the real-view-vector specular), so
	// the raw vertex must write a matching `out vec3 vWorldPos;` or the link fails.
	gl.shaderSource(vs, `#version 300 es
precision highp float;
// Fullscreen triangle — 3 vertices emitted with no input attributes; positions are
// derived from gl_VertexID.  Covers the entire viewport ([-1, -1] .. [1, 3]).
const vec4 POSITIONS[3] = vec4[3](
	vec4(-1.0, -1.0, 0.0, 1.0),
	vec4( 3.0, -1.0, 0.0, 1.0),
	vec4(-1.0,  3.0, 0.0, 1.0)
);
const vec2 UVS[3] = vec2[3](
	vec2(0.0, 0.0),
	vec2(2.0, 0.0),
	vec2(0.0, 2.0)
);
out vec2 vUvLocal;
out vec3 vWorldPos;
void main() {
	vUvLocal = UVS[gl_VertexID];
	vWorldPos = vec3(0.0);
	gl_Position = POSITIONS[gl_VertexID];
}`);
	gl.compileShader(vs);
	if (gl.getShaderParameter(vs, gl.COMPILE_STATUS) !== true) {
		const log = gl.getShaderInfoLog(vs) ?? "(no info log)";
		gl.deleteShader(vs);
		throw new Error(`[TerrainMaterialManager] vertex shader compile failed: ${log}`);
	}
	const fs = gl.createShader(gl.FRAGMENT_SHADER);
	if (!fs) throw new Error("[TerrainMaterialManager] createShader(fragment) failed");
	gl.shaderSource(fs, fragmentSrc);
	gl.compileShader(fs);
	if (gl.getShaderParameter(fs, gl.COMPILE_STATUS) !== true) {
		const log = gl.getShaderInfoLog(fs) ?? "(no info log)";
		gl.deleteShader(fs);
		gl.deleteShader(vs);
		throw new Error(`[TerrainMaterialManager] fragment shader compile failed: ${log}`);
	}
	const program = gl.createProgram();
	if (!program) throw new Error("[TerrainMaterialManager] createProgram failed");
	gl.attachShader(program, vs);
	gl.attachShader(program, fs);
	gl.linkProgram(program);
	gl.deleteShader(fs);
	gl.deleteShader(vs);
	if (gl.getProgramParameter(program, gl.LINK_STATUS) !== true) {
		const log = gl.getProgramInfoLog(program) ?? "(no info log)";
		gl.deleteProgram(program);
		throw new Error(`[TerrainMaterialManager] program link failed: ${log}`);
	}
	return program;
}

/** Build the three per-chunk splat DataTextures from a rasterised SplatChunk: `indexTex`
 *  RGBA8UI (INTEGER texture — usampler2D needs it; normalized RGBA8 returns 1/255 → index 0) +
 *  `weightTex` RGBA32F (slots 0–3) + `weightTex2` RGBA32F (slots 4–7) — the P3 shader's
 *  `u_splatDist`/`u_splatDist2`.  Set7 §9: the 4→8 raise splits the distance field into two
 *  N×N RGBA32F textures (a texel carries 4 lanes), each LINEAR-filtered — the cross-sample
 *  feather interpolation is preserved per lane (a 2-texel-per-sample packed layout would break
 *  bilinear filtering).  Lattice must match the shader's CHUNK_N define (plan §12.1). */
export function buildSplatTextures(chunk: SplatChunk): {indexTex: THREE.DataTexture; weightTex: THREE.DataTexture; weightTex2: THREE.DataTexture} {
	const packed = packSplatChunk(chunk);
	const n = Math.round(Math.sqrt(chunk.indices.length));
	const count = chunk.indices.length;
	const indexTex = new THREE.DataTexture(packed.indicesRGBA, n, n, THREE.RGBAIntegerFormat, THREE.UnsignedByteType);
	// RGBA32F — NOT RedFormat: the shader reads `splatD.<r/g/b/a>` (the ≤4 per-slot signed
	// distances; `packSplatChunk` emits count×4 floats per image, one per material slot).  A
	// RedFormat texture only carries `.r` (d0), so `.g/.b/.a` read 0 and every slot-1+ material
	// would blend at regionWeight(0) = 0.5 — multi-material splats would fade everything to a
	// mush.  The two images are the lane-split halves of the packed distances (slots 0–3 = the
	// first count*4 floats; slots 4–7 = the second count*4 floats).
	const weights0 = packed.weightsFloat.subarray(0, count * 4);
	const weights1 = packed.weightsFloat.subarray(count * 4, count * 8);
	const weightTex = new THREE.DataTexture(weights0, n, n, THREE.RGBAFormat, THREE.FloatType);
	const weightTex2 = new THREE.DataTexture(weights1, n, n, THREE.RGBAFormat, THREE.FloatType);
	// The INDEX texture stays NEAREST + ClampToEdge (integer, non-interpolatable — the shader
	// texelFetches it).  The DISTANCE textures are LINEAR + ClampToEdge — the point of storing
	// distances: the feather band is baked into the lattice's per-texel values, and the filter
	// smooths the near-linear field across chunk edges (texel (CHUNK_N-1) sits exactly on the
	// far edge — both chunks own the same world sample), so filtering across a chunk boundary
	// reads the neighbour's TRUE distance — seam-free by construction (§7).  (The texel size is
	// a fixed screen-px size the user controls via `renderChunk_maxPxPerSample` — not a fixed
	// distance in metres.)
	indexTex.minFilter = THREE.NearestFilter;
	indexTex.magFilter = THREE.NearestFilter;
	weightTex.minFilter = THREE.LinearFilter;
	weightTex.magFilter = THREE.LinearFilter;
	weightTex2.minFilter = THREE.LinearFilter;
	weightTex2.magFilter = THREE.LinearFilter;
	for (const t of [indexTex, weightTex, weightTex2]) {
		t.generateMipmaps = false;
		t.wrapS = THREE.ClampToEdgeWrapping;
		t.wrapT = THREE.ClampToEdgeWrapping;
		t.needsUpdate = true;
	}
	return {indexTex, weightTex, weightTex2};
}

/** Build the per-chunk geometric-normal DataTexture from the worker's normals array (n×n×3,
 *  z-up, slope_scale ALREADY applied — the SAME per-sample normals the old CPU bake applied per
 *  texel).  Packed RGBA32F (xyz + w=1; RGBA32F is more universally sampleable than RGB32F on
 *  mobile GL drivers).  Lattice must match the shader's CHUNK_N define (33 today).  The shader's
 *  matGeoNormal() texelFetches this per fragment at the fragment's lattice coord — the
 *  hillshade + mask-slope input at FINE-CELL granularity (the Phase-3 centre-sample uniform
 *  collapsed both to one slope per chunk).  Dispose on chunk eviction. */
export function buildGeoNormalTexture(normals: Float32Array): THREE.DataTexture {
	const n = Math.round(Math.sqrt(normals.length / 3));
	const count = n * n;
	const data = new Float32Array(count * 4);
	for (let i = 0; i < count; i++) {
		data[i * 4 + 0] = normals[i * 3 + 0] ?? 0;
		data[i * 4 + 1] = normals[i * 3 + 1] ?? 0;
		data[i * 4 + 2] = normals[i * 3 + 2] ?? 1;
		data[i * 4 + 3] = 1;
	}
	const tex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.FloatType);
	tex.minFilter = THREE.NearestFilter;
	tex.magFilter = THREE.NearestFilter;
	tex.generateMipmaps = false;
	tex.wrapS = THREE.ClampToEdgeWrapping;
	tex.wrapT = THREE.ClampToEdgeWrapping;
	tex.needsUpdate = true;
	return tex;
}

/** Build the per-chunk HEIGHT lattice DataTexture (2026-08-13, water elevation-reactive
 *  colour): the worker's heights array (n×n, metres above sea level) as R32F, Nearest +
 *  ClampToEdge — the exact same shape/wiring as {@link buildGeoNormalTexture} (u_geoNormal) and
 *  the ambient-weight mask, so `matGeoHeight()` in the shader samples it with the SAME round()
 *  world→texel mapping (shared lattice → seam-free at chunk boundaries).  Consumed by the
 *  `geoHeight` primitive (the water recipe's large-scale depth colour).  Dispose on eviction. */
export function buildGeoHeightTexture(heights: Float32Array): THREE.DataTexture {
	const n = Math.round(Math.sqrt(heights.length));
	const tex = new THREE.DataTexture(heights, n, n, THREE.RedFormat, THREE.FloatType);
	tex.minFilter = THREE.NearestFilter;
	tex.magFilter = THREE.NearestFilter;
	tex.generateMipmaps = false;
	tex.wrapS = THREE.ClampToEdgeWrapping;
	tex.wrapT = THREE.ClampToEdgeWrapping;
	tex.needsUpdate = true;
	return tex;
}

/** Build the per-chunk ambient-weight mask DataTexture (issue 2, 2026-08-08) from the worker's
 *  CHUNK_N×CHUNK_N multiplier lattice (R32F, Nearest + ClampToEdge).  The shader's
 *  ambientDetailNormal multiplies ∇D by this, silencing 0-multiplier coastline rings. */
export function buildAmbientWeightMaskTexture(mask: Float32Array): THREE.DataTexture {
	const n = Math.round(Math.sqrt(mask.length));
	const tex = new THREE.DataTexture(mask, n, n, THREE.RedFormat, THREE.FloatType);
	tex.minFilter = THREE.NearestFilter;
	tex.magFilter = THREE.NearestFilter;
	tex.generateMipmaps = false;
	tex.wrapS = THREE.ClampToEdgeWrapping;
	tex.wrapT = THREE.ClampToEdgeWrapping;
	tex.needsUpdate = true;
	return tex;
}

/** Build the two per-chunk region DataTextures (Phase 7): `regionTex` RGBA8UI (the WINNING
 *  assignment index per sample — the region-params ROW) + `paramsTex` R32F (one row per region;
 *  columns = the regionModulated params' fixed slots).  `rows` = the number of assignments
 *  (regions); `columns` must match the assembler's REGION_PARAMS_COLUMNS.  NEAREST + ClampToEdge
 *  (texelFetch consumers).  Dispose both on chunk eviction. */
export function buildRegionTextures(
	regions: Uint8Array,
	paramsFloat: Float32Array,
	rows: number,
	columns: number,
): {regionTex: THREE.DataTexture; paramsTex: THREE.DataTexture} {
	const n = Math.round(Math.sqrt(regions.length));
	const regionRGBA = new Uint8Array(regions.length * 4);
	for (let i = 0; i < regions.length; i++) {
		regionRGBA[i * 4 + 0] = regions[i];
		regionRGBA[i * 4 + 1] = regions[i];
		regionRGBA[i * 4 + 2] = regions[i];
		regionRGBA[i * 4 + 3] = regions[i];
	}
	const regionTex = new THREE.DataTexture(regionRGBA, n, n, THREE.RGBAIntegerFormat, THREE.UnsignedByteType);
	const paramsTex = new THREE.DataTexture(paramsFloat, columns, rows, THREE.RedFormat, THREE.FloatType);
	for (const t of [regionTex, paramsTex]) {
		t.minFilter = THREE.NearestFilter;
		t.magFilter = THREE.NearestFilter;
		t.generateMipmaps = false;
		t.wrapS = THREE.ClampToEdgeWrapping;
		t.wrapT = THREE.ClampToEdgeWrapping;
		t.needsUpdate = true;
	}
	return {regionTex, paramsTex};
}